/* Wix SEO title and description preview.
   Wix builds the search title and meta description of every product (or
   store category, blog post or page) from one pattern with variables. These
   functions rebuild those titles from the visitor's pattern and names,
   estimate how wide each title is in a desktop result, and find repeats. The
   page renderer (wix-seo-pattern-preview.cjs), the tests and the browser all
   run this file. Nothing here fetches, stores or uploads what is typed,
   pasted or loaded. */
(function (root, factory) {
  'use strict';
  const shared = typeof module === 'object' && module.exports && typeof require === 'function'
    ? require('./wix-shared.js')
    : root.WixShared;
  const api = factory(shared);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WixPatterns = api;
  if (typeof document !== 'undefined' && typeof document.querySelector === 'function') {
    const start = () => api.init(document.querySelector('[data-wsp-lab]'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (shared) {
  'use strict';

  const META = Object.freeze({
    path: '/tools/wix-seo-pattern-preview',
    name: 'Wix SEO title preview',
    summary: 'See the search titles your Wix SEO pattern makes for every product, which may be cut and which repeat.'
  });

  if (!shared) return Object.freeze({ META, init() {} });
  const { esc, tidyText, graphemes, approximateWidth } = shared;

  // ---- Limits and yardsticks -------------------------------------------------

  // Wix's new product catalog imports up to 10,000 rows per file, so a store's
  // whole list fits. Files are read whole in the browser: past 25 MB a phone
  // can run short of memory.
  const MAX_PRODUCTS = 10000;
  const MAX_FILE_BYTES = 25 * 1024 * 1024;
  const PAGE_SIZE = 50;
  // Google publishes no title width; it cuts titles "typically to fit the
  // device width". 600 px of 20 px Arial is this page's rough desktop estimate.
  const TITLE_WIDTH = 600;
  const TITLE_FONT = Object.freeze({ size: 20 });
  // Wix: "Google usually trims meta descriptions at around 160 characters."
  const SNIPPET_CHARS = 160;
  const ELLIPSIS = ' ...';

  const TYPES = Object.freeze(['product', 'category', 'post', 'page']);
  // Advice is Wix's own, per page type, and says which Wix page it is from:
  // store products and categories "maximum 60" / "maximum 156" characters;
  // other pages "less than 60" characters and a description of "50-160".
  const TYPE_INFO = Object.freeze({
    product: Object.freeze({
      label: 'Product', one: 'product', many: 'products', The: 'The product',
      listLabel: 'Your product names', listOne: 'product name', urlPath: 'product-page',
      title: Object.freeze({ max: 60, strict: false, words: 'at most 60 characters', who: 'Wix’s advice for store products' }),
      desc: Object.freeze({ max: 156, min: 0, words: 'at most 156 characters', who: 'Wix’s advice for store products' }),
      file: 'Load your Wix products CSV', rename: 'shorten the product name in Wix, or give that product its own title in Edit SEO Settings'
    }),
    category: Object.freeze({
      label: 'Category', one: 'category', many: 'categories', The: 'The category',
      listLabel: 'Your category names', listOne: 'category name', urlPath: 'category',
      title: Object.freeze({ max: 60, strict: false, words: 'at most 60 characters', who: 'Wix’s advice for store categories' }),
      desc: Object.freeze({ max: 156, min: 0, words: 'at most 156 characters', who: 'Wix’s advice for store categories' }),
      file: 'Load your Wix categories CSV', rename: 'shorten the category name, or give that category its own title in its SEO settings'
    }),
    post: Object.freeze({
      label: 'Blog post', one: 'post', many: 'posts', The: 'The post title',
      listLabel: 'Your blog post titles', listOne: 'post title', urlPath: 'post',
      title: Object.freeze({ max: 60, strict: true, words: 'less than 60 characters', who: 'Wix’s advice for titles' }),
      desc: Object.freeze({ max: 160, min: 50, words: '50–160 characters', who: 'Wix’s advice for descriptions' }),
      file: 'Load a CSV file', rename: 'shorten the post title, or give that post its own title in its SEO settings'
    }),
    page: Object.freeze({
      label: 'Page', one: 'page', many: 'pages', The: 'The page name',
      listLabel: 'Your page names', listOne: 'page name', urlPath: '',
      title: Object.freeze({ max: 60, strict: true, words: 'less than 60 characters', who: 'Wix’s advice for titles' }),
      desc: Object.freeze({ max: 160, min: 50, words: '50–160 characters', who: 'Wix’s advice for descriptions' }),
      file: 'Load a CSV file', rename: 'shorten the page name, or give that page its own title in its SEO basics'
    })
  });

  // Wix's default patterns. `wix` is how Wix's help writes them; the pipe
  // between the names is plain text (Wix's API example is
  // "{{product.name}} | {{site.name}}"). `title` is the same pattern written
  // the way this preview spells variables. Wix's help names the blog default
  // "Post Name" and its variable list "Post Title": both work here.
  const DEFAULTS = Object.freeze({
    product: Object.freeze({ wix: '{{Product Name | Site Name}}', title: '{{Product Name}} | {{Site Name}}', description: '' }),
    category: Object.freeze({ wix: '{{Category Name | Site Name}}', title: '{{Category Name}} | {{Site Name}}', description: '' }),
    post: Object.freeze({ wix: '{{Post Name | Site Name}}', title: '{{Post Title}} | {{Site Name}}', description: '' }),
    page: Object.freeze({ wix: '{{Page Name | Site Name}}', title: '{{Page Name}} | {{Site Name}}', description: '' })
  });

  // Each page type's variables, by Wix's exact names. `fill` is the list
  // value that fills it in; null means only Wix knows the value, so the
  // preview shows a labelled placeholder instead of making one up. `each`
  // marks values that differ per item (two titles that differ only there
  // are not the same title).
  const v = (name, fill, extra = {}) => Object.freeze({ name, fill, each: fill !== 'site' && !extra.store, ...extra });
  const SITE = v('Site Name', 'site', { help: 'The name you gave your site, from Website Settings.', api: 'site.name' });
  const VARIABLES = Object.freeze({
    product: Object.freeze([
      v('Product Name', 'name', { api: 'product.name' }),
      SITE,
      v('Product Brand', 'brand'),
      v('Product Price', 'price', { help: 'As written in your list or file. Wix may format prices differently.' }),
      v('Product SKU', 'sku'),
      v('Product Description', 'description'),
      v('Product Currency', null, { store: true }),
      v('Product Availability', null),
      v('Main Product Image', null),
      v('Product URL', null)
    ]),
    category: Object.freeze([
      v('Category Name', 'name'),
      SITE,
      v('Category Description', 'description', { help: 'Wix uses only the start of the category description. Its help pages give different lengths, so this preview shows all of it.' }),
      v('Products in Category', null),
      v('Category URL', null),
      v('Category Image', null)
    ]),
    post: Object.freeze([
      v('Post Title', 'name', { aliases: ['Post Name'] }),
      SITE,
      v('Post Description', 'description'),
      v('Post Excerpt', null),
      v('Post Writer', null),
      v('Date Published', null),
      v('Date Last Modified', null),
      v('Post URL', null),
      v('Post Cover Image', null)
    ]),
    page: Object.freeze([
      v('Page Name', 'name', { api: 'page.name' }),
      SITE,
      v('Page URL', null)
    ])
  });

  // Variables Wix lists outside the page-type tables: site, SEO, business and
  // location variables. Only Wix knows their values, so they show as a
  // placeholder and aren't offered as buttons. Title Tag and Meta Description
  // differ per page; the rest are the same on every page.
  const GENERAL = Object.freeze([
    ['General Social Image', false], ['Title Tag', true], ['Meta Description', true],
    ['Business Name', false], ['Business Description', false], ['Business Logo', false],
    ['Country', false], ['State', false], ['City', false], ['Street', false], ['Address Description', false]
  ].map(([name, each]) => Object.freeze({ name, fill: null, each, general: true })));
  // Page Name and Page URL are Wix's general page variables. This preview fills
  // in Page Name only for main pages; elsewhere both are placeholders.
  const PAGE_ONLY = Object.freeze([
    Object.freeze({ name: 'Page Name', fill: null, each: true, pageOnly: true, api: 'page.name' }),
    Object.freeze({ name: 'Page URL', fill: null, each: true, pageOnly: true })
  ]);

  // ---- Small text helpers ------------------------------------------------------

  const text = value => (value == null ? '' : String(value));
  const plural = (count, one, many = `${one}s`) => `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;
  const number = count => count.toLocaleString('en-US');
  const quote = value => `“${value}”`;
  const joinWords = words => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
  const variableText = name => `{{${name}}}`;
  const capital = value => value.charAt(0).toUpperCase() + value.slice(1);
  const normalizeName = value => tidyText(value).toLowerCase();
  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
  const plainText = html => text(html).replace(/<[^>]+>/g, '').replace(/&(?:amp|lt|gt|quot|#39);/g, entity => ENTITIES[entity]);

  // Characters as a reader counts them. Text without combining marks,
  // joiners or characters outside the basic plane has one per code unit, which
  // skips the (slow) grapheme splitter for nearly every product name.
  const CLUSTERS = /[\u0300-\u036f\u0483-\u0489\u0591-\u05c7\u0610-\u061a\u064b-\u065f\u0670\u06d6-\u06ed\u0900-\u0dff\u0e31-\u0e3a\u0e47-\u0e4e\u0eb1-\u0ebc\u0ec8-\u0ecd\u0f18-\u0fbc\u1000-\u109f\u1100-\u11ff\u17b4-\u17d3\u1ab0-\u1aff\u1dc0-\u1dff\u200c\u200d\u20d0-\u20ff\u302a-\u302f\u3099\u309a\ua960-\ua97f\ud7b0-\ud7ff\ufe00-\ufe0f\ufe20-\ufe2f\ud800-\udfff]/;
  function countChars(value) {
    const string = text(value);
    if (!string) return 0;
    return CLUSTERS.test(string) ? graphemes(string).length : string.length;
  }

  // ---- Patterns ------------------------------------------------------------------

  const lookups = {};
  function lookupTable(type) {
    if (lookups[type]) return lookups[type];
    const table = new Map();
    const add = variable => {
      if (table.has(normalizeName(variable.name))) return;
      table.set(normalizeName(variable.name), variable);
      (variable.aliases || []).forEach(alias => table.set(normalizeName(alias), variable));
      if (variable.api) table.set(variable.api, variable);
    };
    VARIABLES[type].forEach(add);
    GENERAL.forEach(add);
    if (type !== 'page') PAGE_ONLY.forEach(add);
    lookups[type] = table;
    return table;
  }
  const lookupVariable = (type, raw) => lookupTable(type).get(normalizeName(raw)) || null;
  // A variable of another page type's own list ({{Category Name}} in a product
  // pattern). Page variables are general in Wix, so they never count here.
  function otherTypeFor(type, raw) {
    const key = normalizeName(raw);
    return TYPES.find(other => {
      if (other === type || other === 'page') return false;
      const found = lookupTable(other).get(key);
      return Boolean(found && found.fill && found.fill !== 'site');
    }) || null;
  }

  // Splits a pattern into text and variables. Accepts Wix's help shorthand
  // ({{Product Name | Site Name}}: two variables with " | " between them),
  // any capitals and extra spaces inside the braces. Reports variables this
  // preview doesn't know, unclosed and extra braces, and single-brace
  // look-alikes.
  function parsePattern(pattern, type = 'product') {
    const source = text(pattern);
    const kind = TYPES.includes(type) ? type : 'product';
    const tokens = [];
    const issues = [];
    let last = 0;
    let at = source.indexOf('{{');
    while (at !== -1) {
      // "{{{Product Name}}}": the last of the opening braces starts the
      // variable; the extra ones stay as text and are reported below.
      let open = at;
      while (source[open + 2] === '{') open++;
      const close = source.indexOf('}}', open + 2);
      if (close === -1) {
        issues.push({ kind: 'unclosed', text: source.slice(at) });
        break;
      }
      // "{{Product Name | {{Site Name}}": the "{{" nearest the "}}" opens it.
      const nested = source.slice(open + 2, close).lastIndexOf('{{');
      if (nested !== -1) {
        open += nested + 2;
        while (source[open + 2] === '{') open++;
      }
      if (open > last) tokens.push({ kind: 'text', text: source.slice(last, open) });
      const inner = source.slice(open + 2, close);
      const whole = source.slice(open, close + 2);
      const pieces = inner.split('|');
      if (pieces.length > 1 && pieces.every(piece => lookupVariable(kind, piece))) {
        pieces.forEach((piece, index) => {
          const variable = lookupVariable(kind, piece);
          if (index) tokens.push({ kind: 'text', text: ' | ' });
          tokens.push({ kind: 'var', variable, text: variableText(variable.name) });
          if (variable.general) issues.push({ kind: 'general', text: variableText(variable.name) });
          if (variable.pageOnly) issues.push({ kind: 'page-only', text: variableText(variable.name) });
        });
        issues.push({ kind: 'shorthand', text: whole, fix: pieces.map(piece => variableText(lookupVariable(kind, piece).name)).join(' | ') });
      } else {
        const variable = lookupVariable(kind, inner);
        if (variable) {
          tokens.push({ kind: 'var', variable, text: whole });
          if (variable.general) issues.push({ kind: 'general', text: whole });
          if (variable.pageOnly) issues.push({ kind: 'page-only', text: whole });
        } else {
          const name = tidyText(inner);
          tokens.push({ kind: 'unknown', name, text: whole });
          issues.push({ kind: name ? 'unknown' : 'empty', name, text: whole, other: name ? otherTypeFor(kind, name) : null });
        }
      }
      last = close + 2;
      at = source.indexOf('{{', last);
    }
    if (last < source.length) tokens.push({ kind: 'text', text: source.slice(last) });
    const unclosed = issues.some(issue => issue.kind === 'unclosed');
    const strays = [];
    tokens.forEach(token => {
      if (token.kind !== 'text') return;
      // "{Product Name}" with single braces is plain text, not a variable.
      const rest = token.text.replace(/\{([^{}\n]{2,60})\}/g, (match, inner) => {
        const variable = lookupVariable(kind, inner);
        if (!variable) return match;
        issues.push({ kind: 'single', text: match, fix: variableText(variable.name) });
        return '';
      });
      // Braces left over ("Shop }}", "{{{Product Name}}}") are plain text too.
      if (!unclosed && /[{}]/.test(rest)) strays.push(token.text.trim() || token.text);
    });
    if (strays.length) issues.push({ kind: 'stray', text: strays.join(' '), parts: strays });
    return { source, type: kind, tokens, issues };
  }

  const tokensToPattern = tokens => tokens.map(token => (token.kind === 'var' ? variableText(token.variable.name) : token.text)).join('');

  // Fills a parsed pattern for one item. Text the preview can't know (a
  // value only Wix has, or a variable it doesn't recognise) shows as ⟨Name⟩.
  function renderTokens(tokens, values) {
    let out = '';
    let placeholders = null;
    let blanks = null;
    let unknown = null;
    let each = false;
    for (let index = 0; index < tokens.length; index++) {
      const token = tokens[index];
      if (token.kind === 'text') { out += token.text; continue; }
      if (token.kind === 'unknown') {
        out += `⟨${token.name || '?'}⟩`;
        (unknown || (unknown = [])).push(token.name);
        each = true;
        continue;
      }
      const variable = token.variable;
      if (!variable.fill) {
        out += `⟨${variable.name}⟩`;
        (placeholders || (placeholders = [])).push(variable.name);
        if (variable.each) each = true;
        continue;
      }
      const value = variable.fill === 'site' ? values.siteName : values[variable.fill];
      const filled = value == null ? '' : String(value);
      if (filled) out += filled;
      else if (variable.fill === 'site') {
        out += '⟨Site Name⟩';
        (placeholders || (placeholders = [])).push(variable.name);
      } else (blanks || (blanks = [])).push(variable.name);
    }
    return { text: tidyText(out), placeholders: placeholders || [], blanks: blanks || [], unknown: unknown || [], each };
  }

  // applyPattern(pattern, values, type): one filled-in title or description.
  function applyPattern(pattern, values = {}, type = 'product') {
    const parsed = pattern && pattern.tokens ? pattern : parsePattern(pattern, type);
    const item = normalizeItem(values);
    return renderTokens(parsed.tokens, { ...item, siteName: tidyText(values.siteName) });
  }

  // ---- Lists: pasted text and CSV files -----------------------------------------

  const ENTITY_NAMES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', hellip: '…', copy: '©', reg: '®', trade: '™', deg: '°', times: '×', eacute: 'é', egrave: 'è', ecirc: 'ê', aacute: 'á', agrave: 'à', acirc: 'â', iacute: 'í', oacute: 'ó', ocirc: 'ô', uacute: 'ú', uuml: 'ü', ouml: 'ö', auml: 'ä', ccedil: 'ç', ntilde: 'ñ', szlig: 'ß', bull: '•', middot: '·', frac12: '½', frac14: '¼', euro: '€', pound: '£' };
  function decodeEntities(value) {
    return text(value).replace(/&(#x[0-9a-f]{1,6}|#\d{1,7}|[a-z]{2,8});/gi, (match, body) => {
      if (body[0] === '#') {
        const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
        return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : match;
      }
      const named = ENTITY_NAMES[body.toLowerCase()];
      return named === undefined ? match : named;
    });
  }

  // Previous-catalog descriptions are HTML ("including HTML tags", Wix says).
  // Only the words matter here; the markup is never shown or run.
  const HTML_TAG = /<\/?[a-z!][^>]*>/i;
  function cleanDescription(value) {
    let out = text(value);
    const html = HTML_TAG.test(out);
    if (html) {
      out = out
        .replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, ' ')
        .replace(/<!--[\s\S]*?-->/g, ' ')
        .replace(/<(br|\/p|\/div|\/li|\/h[1-6]|\/tr|\/td)\b[^>]*>/gi, ' ')
        .replace(/<[^>]*>/g, '');
    }
    return { text: tidyText(decodeEntities(out)), html };
  }

  // Items the parsers have already tidied skip tidying again on every
  // keystroke (descriptions can run to thousands of characters).
  const NORMALIZED = new WeakSet();
  function normalizeItem(item) {
    const source = item && typeof item === 'object' ? item : { name: item };
    const name = text(source.name);
    const clean = {
      name: tidyText(/&[#a-z0-9]+;/i.test(name) ? decodeEntities(name) : name),
      description: tidyText(source.description),
      sku: tidyText(source.sku),
      brand: tidyText(source.brand),
      price: tidyText(source.price)
    };
    NORMALIZED.add(clean);
    return clean;
  }

  const PRICE = /^\s*[$€£¥]?\s*\d{1,7}(?:[.,]\d{1,2})?\s*(?:[$€£¥]|USD|EUR|GBP|CAD|AUD)?\s*$/i;
  const looksLikePrice = value => PRICE.test(text(value));

  // Column names for pasted tables with a header row and for files that are
  // not Wix exports (picked up, never advertised).
  const COLUMN_NAMES = Object.freeze({
    name: ['name', 'title', 'product name', 'product title', 'item name', 'post title', 'page name', 'category name'],
    description: ['plaindescription', 'description', 'body (html)', 'body html', 'body', 'product description', 'post description', 'excerpt'],
    brand: ['brand', 'vendor', 'manufacturer'],
    price: ['price', 'variant price', 'regular price', 'sale price'],
    sku: ['sku', 'variant sku']
  });
  function findColumns(keys) {
    const columns = {};
    Object.keys(COLUMN_NAMES).forEach(field => {
      columns[field] = -1;
      for (const name of COLUMN_NAMES[field]) {
        const index = keys.indexOf(name);
        if (index !== -1) { columns[field] = index; break; }
      }
    });
    return columns;
  }

  // parseProducts(text, type): names typed or pasted one per line. Rows with
  // tabs (pasted from a spreadsheet) are name, then an optional price and a
  // description; a header row maps the columns by name instead.
  function parseProducts(input, type = 'product') {
    const source = text(input).replace(/^\ufeff/, '');
    const empty = { products: [], skipped: 0, lines: 0, header: false, truncated: null };
    if (!source.trim()) return empty;
    const tabs = source.includes('\t');
    const rows = tabs ? shared.parseDelimited(source, '\t') : source.split(/\r\n|\n|\r/).map(line => [line]);
    let start = 0;
    let columns = null;
    const first = (rows[0] || []).map(cell => normalizeName(cell));
    if (first.length > 1 || rows.length > 1) {
      const found = findColumns(first);
      const known = Object.values(found).filter(index => index !== -1).length;
      if (found.name !== -1 && (known > 1 || (first.length === 1 && ['name', 'title', 'product name', 'product title'].includes(first[0])))) {
        columns = found;
        start = 1;
      }
    }
    const products = [];
    let skipped = 0;
    let lines = 0;
    let total = 0;
    for (let index = start; index < rows.length; index++) {
      const cells = rows[index];
      if (!cells.some(cell => text(cell).trim())) continue;
      lines++;
      let item;
      if (columns) {
        const cell = field => (columns[field] === -1 ? '' : cells[columns[field]]);
        item = { name: cell('name'), description: cleanDescription(cell('description')).text, price: cell('price'), brand: cell('brand'), sku: cell('sku') };
      } else {
        item = { name: cells[0] };
        if (cells.length > 1) {
          if (type === 'product' && looksLikePrice(cells[1])) { item.price = cells[1]; item.description = cleanDescription(cells[2]).text; }
          else item.description = cleanDescription(cells[1]).text;
        }
      }
      const clean = normalizeItem(item);
      if (!clean.name) { skipped++; continue; }
      total++;
      if (products.length < MAX_PRODUCTS) products.push(clean);
    }
    return { products, skipped, lines, header: Boolean(columns), truncated: total > MAX_PRODUCTS ? { kept: MAX_PRODUCTS, total } : null };
  }

  // Whether a quoted cell runs to the end of the file: with one quote mark
  // never closed, the rest of the file reads as a single cell. Follows the
  // shared parser's rules: a quote opens a cell only at its start, and two
  // quotes inside a quoted cell are one quote mark.
  function endsInsideQuotes(source, delimiter) {
    let quoted = false;
    let at = source.indexOf('"');
    while (at !== -1) {
      if (quoted) {
        if (source[at + 1] === '"') { at = source.indexOf('"', at + 2); continue; }
        quoted = false;
      } else {
        const before = at === 0 ? '\n' : source[at - 1];
        if (before === delimiter || before === '\n' || before === '\r') quoted = true;
      }
      at = source.indexOf('"', at + 1);
    }
    return quoted;
  }

  // Signs of UTF-8 text read the wrong way: "Ã©" for "é", "â€™" for "’".
  const GARBLED = /\u00c3[\u0080-\u00bf]|\u00e2\u20ac|\u00c2[\u00a0-\u00bf]|\ufffd/;
  const FORMAT_LABELS = Object.freeze({
    new: 'new Wix Stores catalog export',
    previous: 'previous Wix Stores catalog export',
    categories: 'Wix Stores categories export',
    other: 'CSV file (not a Wix export)'
  });

  // parseWixProductsCsv(csvText, options): a Wix Stores product export, in
  // either catalog's format, found by its header names (never by position).
  // New catalog: handle, fieldType (PRODUCT, VARIANT or MEDIA), name, visible,
  // plainDescription, brand, price, sku. Previous: handleId, fieldType
  // (Product or Variant), name, description (HTML), price, sku, visible.
  // Only product rows are products; hidden products are counted, not shown.
  // options.columns ({ name, description } as column numbers) comes from the
  // column picker when the headers aren't recognized.
  function parseWixProductsCsv(csvText, options = {}) {
    const source = text(csvText).replace(/^\ufeff/, '');
    if (!source.trim()) return { ok: false, reason: 'empty', headers: [] };
    // A saved web page or a JSON file, picked by mistake.
    const start = source.slice(0, 400).trimStart();
    if (/^<(?:!doctype|html|head|body|\?xml|!--)/i.test(start)) return { ok: false, reason: 'not-csv', what: 'web page', headers: [] };
    if (/^[{[]\s*["{[\]}]/.test(start)) return { ok: false, reason: 'not-csv', what: 'JSON file', headers: [] };
    const delimiter = shared.detectDelimiter(source);
    const unclosedQuote = endsInsideQuotes(source, delimiter);
    const rows = shared.parseDelimited(source, delimiter);
    let headerAt = 0;
    while (headerAt < rows.length && !rows[headerAt].some(cell => text(cell).trim())) headerAt++;
    const headers = (rows[headerAt] || []).map(cell => tidyText(cell));
    const keys = headers.map(cell => cell.toLowerCase());
    const has = name => keys.includes(name);
    const index = name => keys.indexOf(name);
    let format = 'other';
    if (has('fieldtype') && (has('plaindescription') || has('handle'))) format = 'new';
    else if (has('fieldtype') && (has('handleid') || has('description'))) format = 'previous';
    else if (has('parentpath') || (has('handle') && has('slug') && !has('fieldtype'))) format = 'categories';
    const columns = findColumns(keys);
    if (format === 'new' || format === 'previous') columns.name = index('name');
    columns.fieldType = index('fieldtype');
    columns.visible = index('visible');
    const picked = options.columns || null;
    if (picked) {
      const valid = number => Number.isInteger(number) && number >= 0 && number < headers.length;
      if (valid(picked.name)) columns.name = picked.name;
      columns.description = valid(picked.description) ? picked.description : -1;
    }
    const sample = (rows[headerAt + 1] || []).map(cell => tidyText(cell).slice(0, 60));
    const garbled = GARBLED.test(source.slice(0, 300000));
    if (columns.name === -1) return { ok: false, reason: 'no-name-column', headers, sample, format, garbled, unclosedQuote };
    const counts = { products: 0, variants: 0, media: 0, hidden: 0, noName: 0, other: 0 };
    const products = [];
    const hidden = [];
    let html = 0;
    let total = 0;
    for (let at = headerAt + 1; at < rows.length; at++) {
      const row = rows[at];
      if (!row.some(cell => text(cell).trim())) continue;
      if (columns.fieldType !== -1) {
        const kind = text(row[columns.fieldType]).trim().toUpperCase();
        if (kind === 'VARIANT') { counts.variants++; continue; }
        if (kind === 'MEDIA') { counts.media++; continue; }
        if (kind && kind !== 'PRODUCT') { counts.other++; continue; }
      }
      const name = tidyText(row[columns.name]);
      if (!name) { counts.noName++; continue; }
      if (columns.visible !== -1 && /^(false|no|0)$/i.test(text(row[columns.visible]).trim())) {
        counts.hidden++;
        if (hidden.length < 50) hidden.push(name);
        continue;
      }
      total++;
      counts.products++;
      if (products.length >= MAX_PRODUCTS) continue;
      const description = columns.description === -1 ? { text: '', html: false } : cleanDescription(row[columns.description]);
      if (description.html) html++;
      const cell = field => (columns[field] === -1 ? '' : row[columns[field]]);
      products.push(normalizeItem({ name, description: description.text, brand: cell('brand'), price: cell('price'), sku: cell('sku') }));
    }
    const named = field => (columns[field] === -1 ? null : headers[columns[field]]);
    return {
      ok: products.length > 0,
      reason: products.length ? null : 'no-products',
      format,
      formatLabel: FORMAT_LABELS[format],
      kind: format === 'categories' ? 'categories' : format === 'other' ? 'other' : 'products',
      products,
      counts,
      hidden,
      html,
      garbled,
      unclosedQuote,
      headers,
      sample,
      has: { description: columns.description !== -1, brand: columns.brand !== -1, price: columns.price !== -1, sku: columns.sku !== -1 },
      columns: { name: columns.name, description: columns.description },
      columnNames: { name: named('name'), description: named('description') },
      picked: Boolean(picked),
      truncated: total > MAX_PRODUCTS ? { kept: MAX_PRODUCTS, total } : null
    };
  }

  // ---- Measuring and cutting ------------------------------------------------------

  const SEPARATOR_TAIL = /[\s|·•:;,/\\\-–—]+$/;
  const SEPARATOR_ONLY = /^[\s|·•:;,/\\\-–—]*$/;
  // A separator next to the site name, inside longer text ("… Shipping | ").
  const LEAD_SEPARATOR = /^\s*[|·•:;,/\\\-–—]+\s*/;
  const TAIL_SEPARATOR = /\s*[|·•:;,/\\\-–—]+\s*$/;
  const SPACE = /\s/;
  const splitGraphemes = value => (CLUSTERS.test(value) ? graphemes(value) : Array.from(value));

  // The Arial table's letter widths add up, so with it a cut takes one pass
  // instead of measuring many prefixes (no canvas, the server, the tests).
  const letterWidths = new Map();
  function letterWidth(part, spec) {
    const key = `${spec.size || 20}/${spec.weight || 400}/${part}`;
    let width = letterWidths.get(key);
    if (width === undefined) {
      if (letterWidths.size > 5000) letterWidths.clear();
      width = approximateWidth(part, spec);
      letterWidths.set(key, width);
    }
    return width;
  }

  // Where a line cut to `limit` px would end: whole words, then " ...", the
  // way a search result shortens a title. `visible` is the start of `value`.
  function fitLine(value, limit, measure, spec = TITLE_FONT) {
    const full = text(value);
    const width = full ? measure(full, spec) : 0;
    if (width <= limit + 0.01) return { cut: false, visible: full, hidden: '', width };
    const parts = splitGraphemes(full);
    // Prefixes are measured without the title cache, so they don't fill it.
    const base = measure.base || measure;
    let best = 0;
    if (base === approximateWidth) {
      const dots = approximateWidth(ELLIPSIS, spec);
      let used = 0;
      let kept = 0;
      for (let size = 1; size < parts.length; size++) {
        used += letterWidth(parts[size - 1], spec);
        if (!SPACE.test(parts[size - 1])) kept = used;
        if (kept + dots > limit + 0.01) break;
        best = size;
      }
    } else {
      let low = 0;
      let high = parts.length - 1;
      while (low <= high) {
        const middle = (low + high) >> 1;
        const candidate = parts.slice(0, middle).join('').replace(/\s+$/, '');
        if (base(candidate + ELLIPSIS, spec) <= limit + 0.01) { best = middle; low = middle + 1; } else high = middle - 1;
      }
    }
    let end = best;
    if (end < parts.length && !SPACE.test(parts[end])) {
      let back = end - 1;
      while (back > 0 && !SPACE.test(parts[back])) back--;
      if (back > 0) end = back;
    }
    let visible = parts.slice(0, end).join('').replace(SEPARATOR_TAIL, '');
    if (!visible) visible = parts.slice(0, best).join('').replace(/\s+$/, '');
    return { cut: true, visible, hidden: full.slice(visible.length).trim(), width };
  }

  // A snippet cut at about `max` characters, at a word.
  function cutSnippet(value, max = SNIPPET_CHARS) {
    const full = text(value);
    if (countChars(full) <= max) return { cut: false, visible: full, hidden: '' };
    const parts = splitGraphemes(full.slice(0, max * 4));
    let end = Math.min(max, parts.length);
    if (end < parts.length && !SPACE.test(parts[end])) {
      let back = end - 1;
      while (back > 0 && !SPACE.test(parts[back])) back--;
      if (back > max / 2) end = back;
    }
    const visible = parts.slice(0, end).join('').replace(SEPARATOR_TAIL, '');
    return { cut: true, visible, hidden: full.slice(visible.length).trim() };
  }

  // ---- Preview ------------------------------------------------------------------

  const TITLE_STATUS = Object.freeze({
    fits: Object.freeze({ word: 'Fits', text: 'Within Wix’s advice and likely shown in full' }),
    long: Object.freeze({ word: 'Long', text: 'Longer than Wix’s advice; may be cut' }),
    cut: Object.freeze({ word: 'Likely cut', text: 'Wider than about 600 px; likely cut on desktop' }),
    unknown: Object.freeze({ word: 'Can’t measure', text: 'Can’t measure: part of it is filled in by Wix' }),
    empty: Object.freeze({ word: 'Empty', text: 'Empty: the pattern gives it no title' })
  });
  const DESC_STATUS = Object.freeze({
    none: Object.freeze({ word: 'None', text: 'No description from this pattern' }),
    ok: Object.freeze({ word: 'OK', text: 'Within Wix’s advice' }),
    long: Object.freeze({ word: 'Long', text: 'Longer than Wix’s advice' }),
    short: Object.freeze({ word: 'Short', text: 'Shorter than Wix’s advice' }),
    empty: Object.freeze({ word: 'Empty', text: 'Empty in your list' }),
    unknown: Object.freeze({ word: 'Can’t measure', text: 'Can’t measure: part of it is filled in by Wix' })
  });

  // A 60-character store title is within Wix's store advice ("maximum 60")
  // but not its general advice ("less than 60").
  const EDGE_TEXT = 'Within Wix’s store advice (at most 60); Wix’s general title help says less than 60';
  const titleCheckText = row => (row.title.edge ? EDGE_TEXT : TITLE_STATUS[row.title.status].text);

  function titleStatus(info, rendered, chars, width) {
    if (!rendered.text) return 'empty';
    if (rendered.placeholders.length || rendered.unknown.length) return 'unknown';
    if (width > TITLE_WIDTH) return 'cut';
    if (info.title.strict ? chars >= info.title.max : chars > info.title.max) return 'long';
    return 'fits';
  }

  function descriptionStatus(info, rendered, chars) {
    if (!rendered.text) return 'empty';
    if (rendered.placeholders.length || rendered.unknown.length) return 'unknown';
    if (chars > info.desc.max) return 'long';
    if (info.desc.min && chars < info.desc.min) return 'short';
    return 'ok';
  }

  // The site name and the separator next to it, as one piece of every title.
  // The separator can be text of its own ("{{Product Name}} | {{Site Name}}")
  // or the end of longer text ("… Free Shipping | {{Site Name}}").
  function siteNamePiece(tokens, siteName) {
    if (!siteName) return null;
    const at = tokens.findIndex(token => token.kind === 'var' && token.variable.fill === 'site');
    if (at === -1) return null;
    const rest = tokens.slice();
    const prev = tokens[at - 1];
    const next = tokens[at + 1];
    const tail = prev && prev.kind === 'text' ? TAIL_SEPARATOR.exec(prev.text) : null;
    const lead = !tail && next && next.kind === 'text' ? LEAD_SEPARATOR.exec(next.text) : null;
    let piece = siteName;
    let sign = '';
    if (tail) {
      piece = tail[0] + siteName;
      sign = tail[0].trim();
      rest[at - 1] = { kind: 'text', text: prev.text.slice(0, tail.index) };
    } else if (lead) {
      piece = siteName + lead[0];
      sign = lead[0].trim();
      rest[at + 1] = { kind: 'text', text: next.text.slice(lead[0].length) };
    }
    rest.splice(at, 1);
    const kept = rest.filter(token => token.kind !== 'text' || token.text);
    const pattern = tokensToPattern(kept).replace(LEAD_SEPARATOR, '').replace(TAIL_SEPARATOR, '').trim();
    return { piece, sign, before: Boolean(tail), rest: kept, pattern };
  }

  // Title widths, remembered per measuring function: editing the description
  // pattern or the filters doesn't measure thousands of titles again.
  const widthCaches = new WeakMap();
  function rememberWidths(measureFn) {
    let cache = widthCaches.get(measureFn);
    if (!cache) { cache = new Map(); widthCaches.set(measureFn, cache); }
    const remembered = (value, spec) => {
      if (spec !== TITLE_FONT) return measureFn(value, spec);
      let width = cache.get(value);
      if (width === undefined) {
        if (cache.size > 60000) cache.clear();
        width = measureFn(value, spec);
        cache.set(value, width);
      }
      return width;
    };
    remembered.base = measureFn;
    return remembered;
  }

  // preview(settings, products, measure): every item's title and description,
  // their checks and the repeats. settings: { type, siteName, title,
  // description }. An empty title pattern is Wix's default (Wix: empty the
  // field and save to reset it); an empty description pattern gives none.
  function preview(settings = {}, products = [], measure) {
    const type = TYPES.includes(settings.type) ? settings.type : 'product';
    const info = TYPE_INFO[type];
    const measureFn = rememberWidths(typeof measure === 'function' ? measure : approximateWidth);
    const siteName = tidyText(settings.siteName);
    const typedTitle = settings.title == null ? DEFAULTS[type].title : text(settings.title);
    const titleDefaulted = !typedTitle.trim();
    const title = parsePattern(titleDefaulted ? DEFAULTS[type].title : typedTitle, type);
    const typedDescription = settings.description == null ? DEFAULTS[type].description : text(settings.description);
    const description = typedDescription.trim() ? parsePattern(typedDescription, type) : null;
    const list = Array.isArray(products) ? products.slice(0, MAX_PRODUCTS) : [];
    const rows = new Array(list.length);
    const titleKeys = new Map();
    const descKeys = new Map();
    const blanks = new Map();
    const counts = { total: list.length, fits: 0, long: 0, cut: 0, unknown: 0, empty: 0, sameTitle: 0, desc: { none: 0, ok: 0, long: 0, short: 0, empty: 0, unknown: 0 }, sameDescription: 0 };
    const placeholders = new Set();
    const unknownNames = new Set();
    const source = settings.source && typeof settings.source === 'object' ? settings.source : { kind: 'list' };
    for (let index = 0; index < list.length; index++) {
      const item = NORMALIZED.has(list[index]) ? list[index] : normalizeItem(list[index]);
      const values = { name: item.name, description: item.description, sku: item.sku, brand: item.brand, price: item.price, siteName };
      const rendered = renderTokens(title.tokens, values);
      const chars = countChars(rendered.text);
      const width = rendered.text ? measureFn(rendered.text, TITLE_FONT) : 0;
      const status = titleStatus(info, rendered, chars, width);
      counts[status]++;
      rendered.placeholders.forEach(name => placeholders.add(name));
      rendered.unknown.forEach(name => unknownNames.add(name));
      rendered.blanks.forEach(name => blanks.set(`title\u0000${name}`, (blanks.get(`title\u0000${name}`) || 0) + 1));
      const edge = status === 'fits' && !info.title.strict && chars === info.title.max;
      const row = { index, item, title: { text: rendered.text, chars, width, status, edge, placeholders: rendered.placeholders, blanks: rendered.blanks }, sameTitle: null, sameDescription: null };
      if (rendered.text && !rendered.each) {
        const key = rendered.text.toLowerCase();
        const seen = titleKeys.get(key);
        if (seen) seen.push(index); else titleKeys.set(key, [index]);
      }
      if (!description) {
        row.description = { text: '', chars: 0, status: 'none', placeholders: [], blanks: [] };
        counts.desc.none++;
      } else {
        const filled = renderTokens(description.tokens, values);
        const descChars = countChars(filled.text);
        const descStatus = descriptionStatus(info, filled, descChars);
        counts.desc[descStatus]++;
        filled.placeholders.forEach(name => placeholders.add(name));
        filled.unknown.forEach(name => unknownNames.add(name));
        filled.blanks.forEach(name => blanks.set(`description\u0000${name}`, (blanks.get(`description\u0000${name}`) || 0) + 1));
        row.description = { text: filled.text, chars: descChars, status: descStatus, placeholders: filled.placeholders, blanks: filled.blanks };
        if (filled.text && !filled.each) {
          const key = filled.text.toLowerCase();
          const seen = descKeys.get(key);
          if (seen) seen.push(index); else descKeys.set(key, [index]);
        }
      }
      rows[index] = row;
    }
    const groups = (keys, field) => {
      const found = [];
      keys.forEach(indexes => {
        if (indexes.length < 2) return;
        const group = { id: found.length, rows: indexes };
        indexes.forEach(at => { rows[at][field] = group; });
        found.push(group);
      });
      return found;
    };
    const sameTitle = groups(titleKeys, 'sameTitle');
    const sameDescription = groups(descKeys, 'sameDescription');
    counts.sameTitle = sameTitle.reduce((sum, group) => sum + group.rows.length, 0);
    counts.sameDescription = sameDescription.reduce((sum, group) => sum + group.rows.length, 0);

    // Why each cut title is cut: a name too wide on its own (no pattern can
    // fix that), the site name (it would fit without it), or the rest of
    // what the pattern adds. Widths add up along a line, so a title without
    // the site name is the title minus that piece (within a pixel of
    // kerning), without measuring thousands of titles again.
    const hasName = title.tokens.some(token => token.kind === 'var' && token.variable.fill === 'name');
    const site = siteNamePiece(title.tokens, siteName);
    const pieceWidth = site ? measureFn(site.piece, TITLE_FONT) : 0;
    const tooLong = [];
    const patternLong = [];
    let saved = 0;
    rows.forEach(row => {
      if (row.title.status !== 'cut') return;
      const fitsWithoutSite = Boolean(site) && row.title.width - pieceWidth <= TITLE_WIDTH;
      if (fitsWithoutSite) saved++;
      if (hasName && row.item.name && measureFn(row.item.name, TITLE_FONT) > TITLE_WIDTH) tooLong.push(row.index);
      else if (!fitsWithoutSite) patternLong.push(row.index);
    });
    const siteCost = site ? { piece: site.piece, sign: site.sign, before: site.before, width: pieceWidth, saved, pattern: site.pattern } : null;
    const first = title.tokens.find(token => token.kind !== 'text' || token.text.trim());
    return {
      type,
      info,
      siteName,
      settings: { type, siteName, title: typedTitle, description: typedDescription },
      titleDefaulted,
      title,
      description,
      rows,
      counts,
      sameTitle,
      sameDescription,
      blanks: [...blanks].map(([key, count]) => ({ field: key.split('\u0000')[0], name: key.split('\u0000')[1], count })),
      placeholders: [...placeholders],
      unknown: [...unknownNames],
      siteCost,
      tooLong,
      patternLong,
      source,
      hasName,
      nameFirst: Boolean(first && first.kind === 'var' && first.variable.fill === 'name'),
      measure: measureFn
    };
  }

  // The cut for one row, worked out when a row is shown (cutting means
  // measuring many prefixes, so it isn't done for rows nobody looks at).
  function fitFor(result, row) {
    if (!row.fit) row.fit = row.title.status === 'cut' ? fitLine(row.title.text, TITLE_WIDTH, result.measure) : { cut: false, visible: row.title.text, hidden: '', width: row.title.width };
    return row.fit;
  }

  const widest = result => result.rows.reduce((best, row) => (!best || row.title.width > best.title.width ? row : best), null);
  const narrowest = result => result.rows.reduce((best, row) => (row.title.text && (!best || row.title.width < best.title.width) ? row : best), null);

  // ---- Words ----------------------------------------------------------------------

  const isAre = count => (count === 1 ? 'is' : 'are');
  const listWord = result => (result.source && result.source.kind === 'file' ? 'file' : 'list');
  const rowList = indexes => joinWords(indexes.slice(0, 4).map(index => String(index + 1))) + (indexes.length > 4 ? ` and ${number(indexes.length - 4)} more` : '');
  const rowsWord = indexes => `${indexes.length === 1 ? 'row' : 'rows'} ${rowList(indexes)}`;

  // The answer, first: how many titles show in full, then what doesn't.
  function summarySentences(result) {
    const { counts, info } = result;
    const total = counts.total;
    if (!total) return [`Add ${info.listOne}s to see their titles.`];
    const sentences = [];
    const siteMissing = !result.siteName && result.title.tokens.some(token => token.kind === 'var' && token.variable.fill === 'site');
    if (siteMissing) sentences.push('Type your site name to measure these titles: every one includes it.');
    else if (counts.fits === total) sentences.push(total === 1 ? 'This title should show in full.' : `All ${number(total)} titles should show in full.`);
    else if (counts.unknown === total) {
      const why = result.unknown.length ? 'the pattern has a variable this preview doesn’t know' : `part of ${total === 1 ? 'it' : 'each'} is filled in by Wix`;
      sentences.push(`${total === 1 ? 'This title' : `These ${number(total)} titles`} can’t be measured: ${why}.`);
    }
    else {
      sentences.push(`${number(counts.fits)} of ${number(total)} titles should show in full.`);
      if (counts.cut) sentences.push(`${number(counts.cut)} ${isAre(counts.cut)} likely cut on desktop.`);
      // Cut titles are often over Wix's character advice too, so the long
      // ones are the rest: over the advice but likely shown in full.
      if (counts.long) sentences.push(`${number(counts.long)}${counts.cut ? ' more' : ''} ${isAre(counts.long)} longer than Wix’s advice (${info.title.words}) but likely ${counts.long === 1 ? 'fits' : 'fit'} on desktop.`);
      if (counts.unknown) sentences.push(`${number(counts.unknown)} can’t be measured.`);
      if (counts.empty) sentences.push(`${number(counts.empty)} ${counts.empty === 1 ? 'has' : 'have'} no title.`);
    }
    if (counts.sameTitle) sentences.push(`${plural(counts.sameTitle, info.one, info.many)} would get the same title.`);
    return sentences;
  }
  const summaryText = result => summarySentences(result).join(' ');

  function descriptionLine(result) {
    const { counts, info, type } = result;
    if (!counts.total) return '';
    if (!result.description) {
      if (type === 'product' || type === 'category') return `No description pattern. Wix’s pages disagree on what ${info.many} get by default.`;
      return `No description pattern, which is Wix’s default for ${type === 'post' ? 'blog posts' : 'main pages'}.`;
    }
    const d = counts.desc;
    const parts = [];
    if (d.empty) parts.push(`${number(d.empty)} ${d.empty === 1 ? 'is' : 'are'} empty in your ${listWord(result)}`);
    if (counts.sameDescription) parts.push(`${plural(counts.sameDescription, info.one, info.many)} share one`);
    if (d.long) parts.push(`${number(d.long)} ${isAre(d.long)} longer than Wix’s advice`);
    if (d.short) parts.push(`${number(d.short)} ${isAre(d.short)} shorter than Wix’s advice`);
    if (d.unknown) parts.push(`${number(d.unknown)} can’t be measured`);
    if (!parts.length) return counts.total === 1 ? 'Description: within Wix’s advice.' : `Descriptions: all ${number(counts.total)} are within Wix’s advice.`;
    return `Descriptions: ${joinWords(parts)}.`;
  }

  function summaryMarkup(result) {
    const [lead, ...rest] = summarySentences(result);
    const match = /^(\d[\d,]* of \d[\d,]* titles|All \d[\d,]* titles)(.*)$/.exec(lead);
    const first = match ? `<strong>${esc(match[1])}</strong>${esc(match[2])}` : esc(lead);
    return `<p class="wsp-summary" data-wsp-summary-text>${first}${rest.length ? ` ${esc(rest.join(' '))}` : ''}</p>${descriptionLine(result) ? `<p class="wsp-summary-desc">${esc(descriptionLine(result))}</p>` : ''}`;
  }

  // ---- The drawn result -------------------------------------------------------------

  function urlLine(result, row) {
    const path = result.info.urlPath;
    if (result.type === 'page' && row && /^home$/i.test(row.item.name)) return 'https://www.example.com';
    return `https://www.example.com › ${path ? `${path} › ` : ''}...`;
  }

  const NO_DESCRIPTION = 'No description from this pattern. Search engines may show other text from the page.';

  // What the drawing shows for one row, as plain strings (the /tools card
  // and the tests read the same values the page prints).
  function serpData(result, row) {
    const fit = fitFor(result, row);
    const status = row.title.status;
    const shown = fit.cut ? fit.visible + ELLIPSIS : row.title.text;
    let snippet = NO_DESCRIPTION;
    let snippetState = 'none';
    if (result.description) {
      if (!row.description.text) { snippet = `No description in your ${listWord(result)}. Search engines may show other text from the page.`; snippetState = 'empty'; }
      else {
        const cut = cutSnippet(row.description.text);
        snippet = cut.cut ? cut.visible + ELLIPSIS : cut.visible;
        snippetState = 'text';
      }
    }
    return {
      siteName: result.siteName || 'Your site',
      url: urlLine(result, row),
      title: shown,
      cutAfter: fit.cut ? fit.visible : null,
      hidden: fit.cut ? fit.hidden : '',
      status,
      widthStatus: TITLE_STATUS[status].word,
      snippet,
      snippetState
    };
  }

  const GLOBE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.6 2.6 3.8 5.7 3.8 9s-1.2 6.4-3.8 9c-2.6-2.6-3.8-5.7-3.8-9S9.4 5.6 12 3z"/></svg>';

  function measureWords(result, row) {
    const width = `${number(Math.round(row.title.width))} px`;
    const chars = plural(row.title.chars, 'character');
    return { width, chars };
  }

  // One drawn desktop result (decorative; the caption carries the words).
  function serpMarkup(result, row) {
    const data = serpData(result, row);
    const status = row.title.status;
    const { width, chars } = measureWords(result, row);
    let caption;
    if (status === 'cut') caption = `${width} wide, ${chars}. Likely hidden: ${quote(esc(data.hidden))}`;
    else if (status === 'long') caption = `${width} wide, ${chars}. ${esc(capital(result.info.title.who))}: ${esc(result.info.title.words)}.`;
    else if (status === 'fits' && row.title.edge) caption = `${width} wide, ${chars}. ${esc(EDGE_TEXT)}.`;
    else if (status === 'fits') caption = `${width} wide, ${chars}.`;
    else if (status === 'unknown' && !result.siteName && row.title.placeholders.includes('Site Name') && row.title.placeholders.every(name => name === 'Site Name')) caption = 'Type your site name above to measure it.';
    else if (status === 'unknown' && row.title.placeholders.length) caption = `Wix fills in ${esc(joinWords([...new Set(row.title.placeholders)].map(name => `⟨${name}⟩`)))}, so it can’t be measured here.`;
    else if (status === 'unknown') caption = 'The pattern has a variable this preview doesn’t know, so it can’t be measured.';
    else caption = 'The pattern gives this one no title.';
    return `<div class="wsp-serp" aria-hidden="true">
            <div class="wsp-serp-head"><span class="wsp-serp-icon">${GLOBE}</span><span class="wsp-serp-site"><span class="wsp-serp-name">${esc(data.siteName)}</span><span class="wsp-serp-url">${esc(data.url)}</span></span></div>
            <p class="wsp-serp-title" dir="auto">${esc(data.title || ' ')}</p>
            <p class="wsp-serp-snippet" dir="auto" data-state="${data.snippetState}">${esc(data.snippet)}</p>
          </div>
          <p class="wsp-visually-hidden">Drawn result. Title shown as ${quote(esc(data.title))}. ${esc(data.snippetState === 'text' ? `Description shown as ${quote(data.snippet)}.` : data.snippet)}</p>
          <p class="wsp-serp-caption"><span class="wsp-chip" data-level="${status}"><span class="wsp-chip-icon" aria-hidden="true"></span>${esc(TITLE_STATUS[status].word)}</span> <span>${caption}</span></p>`;
  }

  // ---- What to change -------------------------------------------------------------

  const LEVEL_ORDER = { fix: 0, check: 1, note: 2, ok: 3 };
  const LEVEL_WORDS = { fix: 'To fix', check: 'Check', note: 'Note', ok: 'OK' };
  const code = value => `<code>${esc(value)}</code>`;
  const nameVariable = type => VARIABLES[type].find(variable => variable.fill === 'name').name;
  const descVariable = type => (VARIABLES[type].find(variable => variable.fill === 'description') || {}).name;

  // Suggestions built only from what the preview found and what Wix and
  // Google say. Each has a level, a title, a detail and maybe a one-tap action.
  const GOOGLE_SAME_DESCRIPTION = 'Google says: “Identical or similar descriptions on every page of a site aren’t helpful when individual pages appear in search results.”';
  const COLUMN_WORDS = Object.freeze({ brand: 'brand', price: 'price', sku: 'SKU', description: 'description' });

  function suggestions(result) {
    const out = [];
    const add = (id, level, title, detail, action) => out.push({ id, level, title, detail, action });
    const { counts, info, type } = result;
    if (!counts.total) return out;
    const nameVar = nameVariable(type);
    const nameWord = type === 'post' ? 'post title' : `${info.one} name`;
    const list = listWord(result);
    // Unknown variables are explained under the pattern field itself.
    if (!result.siteName && result.title.tokens.some(token => token.kind === 'var' && token.variable.fill === 'site')) {
      add('site-name', 'fix', 'Add your site name', 'Every title in this pattern includes it, so none can be measured until it’s filled in.', { id: 'focus-site', label: 'Type your site name' });
    }
    if (counts.sameTitle) {
      const group = result.sameTitle[0];
      const sample = result.rows[group.rows[0]].title.text;
      const more = result.sameTitle.length > 1 ? ` That’s ${plural(result.sameTitle.length, 'group')} of repeats; the first is ${quote(esc(sample))} (${rowsWord(group.rows)}).` : ` ${quote(esc(sample))} (${rowsWord(group.rows)}).`;
      if (!result.hasName) add('same-title', 'fix', `${esc(plural(counts.sameTitle, info.one, info.many))} would get the same title`, `Your pattern doesn’t use ${code(variableText(nameVar))}, so it can’t tell them apart.${more}`, { id: 'name-first', label: `Add ${variableText(nameVar)} to the start` });
      else add('same-title', 'fix', `${esc(plural(counts.sameTitle, info.one, info.many))} would get the same title`, `Their ${type === 'post' ? 'titles' : 'names'} are the same.${more} Rename one, or add what makes it different, like the size or color.`);
    }
    const site = result.siteCost;
    if (site) {
      const px = Math.round(site.width);
      const share = Math.round((site.width / TITLE_WIDTH) * 100);
      const what = site.sign ? `Your site name and the ${quote(esc(site.sign))} ${site.before ? 'before' : 'after'} it take` : 'Your site name takes';
      // Google: "Google may omit the site name from the title link, if it's
      // repetitive with the site name that's already shown in the search
      // result." Wix: "It's also a good idea to include the name of your
      // business or site in the title tag."
      const cut = counts.cut
        ? ` Without it, ${number(site.saved)} of the ${plural(counts.cut, 'title')} likely cut would fit. Google says it “may omit the site name from the title link, if it’s repetitive” with the site name already shown in the result. So a title marked likely cut can still show in full.`
        : '';
      add('site-cost', counts.cut ? 'check' : 'note', `${what} about ${number(px)} px of every title`,
        `That’s ${share}% of the 600 px estimate.${cut} Wix suggests keeping your business or site name in titles.`,
        { id: 'drop-site', label: 'Preview without the site name' });
    }
    if (result.tooLong.length) {
      const names = result.tooLong.slice(0, 3).map(index => quote(esc(result.rows[index].item.name)));
      const all = result.tooLong.length;
      add('too-long', 'check', `${number(all)} ${nameWord}${all === 1 ? ' is' : 's are'} too long for any pattern`,
        `On ${all === 1 ? 'its' : 'their'} own, ${all === 1 ? 'it is' : 'they are'} wider than about 600 px: ${joinWords(names)}${all > 3 ? ` and ${number(all - 3)} more` : ''}. A pattern can’t shorten one name: ${esc(info.rename)}.`);
    }
    if (result.patternLong.length) {
      const all = result.patternLong.length;
      add('pattern-long', 'check', `${plural(all, 'title')} ${isAre(all)} cut because of what the pattern adds`,
        `The ${all === 1 ? `${nameWord} fits` : `${nameWord}s fit`} on ${all === 1 ? 'its' : 'their'} own. With the pattern’s other words, ${all === 1 ? 'the title is' : 'the titles are'} wider than about 600 px${site ? ', even without the site name' : ''}. Remove words or variables from the title pattern.`);
    }
    if (counts.long) {
      add('long', 'check', `${plural(counts.long, 'title')} ${isAre(counts.long)} longer than Wix’s advice but likely ${counts.long === 1 ? 'fits' : 'fit'} on desktop`,
        `${esc(capital(info.title.who))} is ${esc(info.title.words)}. Google cuts titles to fit the device width, so a narrower screen can cut ${counts.long === 1 ? 'it' : 'them'}.`);
    }
    if (result.hasName && !result.nameFirst) {
      add('name-first', 'check', `Put the ${esc(nameWord)} first`,
        `When a title is cut, the end goes missing, and in your pattern the ${esc(nameWord)} comes after other words.`, { id: 'name-first', label: `Move ${variableText(nameVar)} to the start` });
    } else if (result.hasName && (counts.cut || counts.long)) {
      add('name-first', 'ok', `${esc(info.The)}${type === 'post' || type === 'page' ? '' : ' name'} comes first`, `When a title is cut, the end goes missing, so the ${type === 'post' ? 'title' : 'name'} stays in view.`);
    }
    // An empty description variable is the "no description" note below.
    const source = result.source || {};
    result.blanks.filter(blank => !(blank.field === 'description' && blank.name === descVariable(type))).forEach(({ field, name, count }) => {
      const fill = (lookupVariable(type, name) || {}).fill;
      const noColumn = source.kind === 'file' && source.has && fill in source.has && !source.has[fill];
      const gap = noColumn
        ? `This file has no ${COLUMN_WORDS[fill]} column${source.format === 'previous' && fill === 'brand' ? ' (Wix’s previous catalog export has none)' : ''}, so this preview leaves it out of the ${field}; Wix may still have one.`
        : `Your ${list} has no value for it, so this preview leaves it out of the ${field}; Wix may still have one.`;
      add(`blank-${field}-${name}`, 'check', `${esc(variableText(name))} is empty in your ${list} for ${esc(plural(count, info.one, info.many))}`,
        `${gap} Wix’s help doesn’t say what an empty variable shows. To check one, use Preview on Google in that ${esc(info.one)}’s SEO settings.`);
    });
    const usesPrice = [result.title, result.description].some(parsed => parsed && parsed.tokens.some(token => token.kind === 'var' && token.variable.fill === 'price'));
    if (usesPrice) add('price', 'note', 'Prices show as written in your list', 'Wix’s help doesn’t say how it writes a price in a title, for example with a currency sign, so the real title may differ a little.');
    const waiting = result.placeholders.filter(name => name !== 'Site Name');
    if (waiting.length) {
      add('placeholders', 'note', `Wix fills in ${esc(joinWords(waiting.map(name => `⟨${name}⟩`)))}`, 'This preview can’t know those values, so titles and descriptions with them can’t be measured here.');
    }
    // Descriptions. Patterns are set in Wix with + Add Variable, never typed.
    const descVar = descVariable(type);
    const howToSet = descVar ? `To set one in Wix, add the ${descVar} variable with + Add Variable in the Meta description field.` : '';
    if (!result.description) {
      if (type === 'product' || type === 'category') {
        add('no-description', 'note', 'No description pattern',
          `${type === 'product'
            ? 'Wix’s pages disagree on the default: its product SEO article says a product gets its product description, and its defaults table says blank.'
            : 'Wix’s pages disagree on the default: its category article says the start of the category description, and its defaults table says blank.'} ${howToSet}`,
          { id: 'use-description', label: `Use the ${info.one} description` });
      } else if (type === 'post') {
        add('no-description', 'note', 'No description pattern', `Wix’s default for blog posts is blank. ${howToSet}`, { id: 'use-description', label: 'Use the post description' });
      } else {
        add('no-description', 'note', 'No description pattern', 'Wix’s default for main pages is blank. You can write a description for each page in its SEO basics.');
      }
    } else {
      const d = counts.desc;
      const tokens = result.description.tokens;
      const perItem = tokens.some(token => token.kind === 'unknown' || (token.kind === 'var' && ((token.variable.fill && token.variable.fill !== 'site') || token.variable.each)));
      const usesDescription = tokens.some(token => token.kind === 'var' && token.variable.fill === 'description');
      if (!perItem && counts.total > 1) {
        add('same-description', 'check', `Every ${esc(info.one)} gets the same description`,
          `Your description pattern has no ${esc(info.one)} variable, so every ${esc(info.one)} gets the same text. ${GOOGLE_SAME_DESCRIPTION}`,
          descVar ? { id: 'use-description', label: `Use the ${type === 'post' ? 'post' : info.one} description` } : undefined);
      } else if (counts.sameDescription) {
        const group = result.sameDescription[0];
        const sample = result.rows[group.rows[0]].description.text;
        const short = countChars(sample) > 70 ? `${cutSnippet(sample, 70).visible}…` : sample;
        // Wix's advice is about shared product descriptions; other repeats
        // get Google's general advice.
        const why = usesDescription ? 'Wix advises: “Avoid using descriptions from manufacturers to prevent duplicate content issues.”' : GOOGLE_SAME_DESCRIPTION;
        add('same-description', 'check', `Same description on ${esc(plural(counts.sameDescription, info.one, info.many))}`,
          `${quote(esc(short))} (${rowsWord(group.rows)})${result.sameDescription.length > 1 ? ` and ${plural(result.sameDescription.length - 1, 'more group')}` : ''}. ${why}`);
      }
      if (d.long) {
        add('description-long', 'check', `${plural(d.long, 'description')} ${isAre(d.long)} longer than Wix’s advice`,
          `${esc(capital(info.desc.who))} is ${esc(info.desc.words)}. Google says there’s no limit but cuts the snippet to fit the device width, so put what matters first.`);
      }
      if (d.short) add('description-short', 'note', `${plural(d.short, 'description')} ${isAre(d.short)} shorter than Wix’s advice`, `${esc(capital(info.desc.who))} is ${esc(info.desc.words)}.`);
      if (d.empty) {
        const none = d.empty === 1 ? 'has' : 'have';
        add('description-empty', 'note', usesDescription ? `${esc(plural(d.empty, info.one, info.many))} ${none} no description in your ${list}` : `${esc(plural(d.empty, info.one, info.many))} would get an empty description`,
          `If Wix has no value either, ${d.empty === 1 ? 'its' : 'their'} meta description would be empty${type === 'post' ? ', which is Wix’s default for blog posts' : ''}. Search engines may show other text from the page.`);
      }
      if (descVar === 'Category Description' && tokens.some(token => token.kind === 'var' && token.variable.name === 'Category Description')) {
        add('category-description', 'note', 'Wix uses only the start of a category description', 'Its help pages give different lengths, so this preview shows the whole description.');
      }
    }
    if (type === 'page' && result.rows.some(row => /^home$/i.test(row.item.name))) {
      add('home', 'note', 'Wix’s pages disagree on the homepage title', 'One says the site name alone is the default; two say the page name and site name, like “Home | My Stunning Website”. Check your homepage’s Preview on Google.');
    }
    return out.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  }

  function suggestionsMarkup(result) {
    return suggestions(result).map(item => `<li class="wsp-change" data-level="${item.level}" data-change="${esc(item.id)}"><span class="wsp-change-icon" aria-hidden="true"></span><div><p class="wsp-change-title"><span class="wsp-visually-hidden">${LEVEL_WORDS[item.level]}: </span>${item.title}</p><p class="wsp-change-detail">${item.detail}</p>${item.action ? `<button type="button" class="wsp-fix" data-wsp-action="${esc(item.action.id)}">${esc(item.action.label)}</button>` : ''}</div></li>`).join('');
  }

  // ---- The table ----------------------------------------------------------------

  const FILTERS = Object.freeze([
    ['all', 'All', () => true],
    ['cut', 'Likely cut', row => row.title.status === 'cut'],
    ['long', 'Long but fits', row => row.title.status === 'long'],
    ['same-title', 'Same title', row => Boolean(row.sameTitle)],
    ['empty-description', 'Empty description', row => row.description.status === 'empty'],
    ['same-description', 'Same description', row => Boolean(row.sameDescription)]
  ]);

  function filterCounts(result) {
    return FILTERS.map(([id, label, test]) => ({ id, label, count: id === 'all' ? result.rows.length : result.rows.filter(test).length }));
  }

  function filtersMarkup(result, view = {}) {
    const active = view.filter || 'all';
    const list = filterCounts(result).filter(entry => entry.id === 'all' || entry.count || entry.id === active);
    if (list.length < 2) return '';
    return list.map(entry => `<button type="button" class="wsp-filter" data-wsp-filter="${entry.id}" aria-pressed="${entry.id === active}">${esc(entry.label)} <span class="wsp-filter-count">${number(entry.count)}</span></button>`).join('');
  }

  function visibleRows(result, view = {}) {
    const filter = FILTERS.find(([id]) => id === (view.filter || 'all')) || FILTERS[0];
    let rows = result.rows.filter(filter[2]);
    if (view.sort === 'width') rows = rows.slice().sort((a, b) => b.title.width - a.title.width || a.index - b.index);
    return rows;
  }

  function titleCellMarkup(result, row) {
    const fit = fitFor(result, row);
    if (!row.title.text) return '<span class="wsp-t-empty">No title</span>';
    // dir="auto": a right-to-left title keeps its start, and its cut, in place.
    if (!fit.cut) return `<span class="wsp-t" dir="auto"><span class="wsp-t-shown">${esc(row.title.text)}</span></span>`;
    return `<span class="wsp-t" dir="auto"><span class="wsp-t-shown">${esc(fit.visible)}</span> <span class="wsp-cut-mark" aria-hidden="true">${SCISSORS}</span><span class="wsp-visually-hidden">Likely cut from here: </span><span class="wsp-t-cut">${esc(fit.hidden)}</span></span>`;
  }

  const SCISSORS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="6" cy="6" r="2.6"/><circle cx="6" cy="18" r="2.6"/><path d="M8.2 7.4 20 17M8.2 16.6 20 7"/></svg>';
  const chip = (status, words) => `<span class="wsp-chip" data-level="${status}"><span class="wsp-chip-icon" aria-hidden="true"></span>${esc(words)}</span>`;

  function descriptionCell(result, row) {
    const d = row.description;
    const same = row.sameDescription ? `<span class="wsp-same">Same as ${rowsWord(row.sameDescription.rows.filter(index => index !== row.index))}</span>` : '';
    const chars = d.status === 'none' || d.status === 'empty' ? '' : `<span class="wsp-chars">${esc(plural(d.chars, 'character'))}</span>`;
    return `<span class="wsp-cell-label">Description</span>${chip(`d-${d.status}`, DESC_STATUS[d.status].word)}${chars}${same}`;
  }

  function detailMarkup(result, row) {
    const fit = fitFor(result, row);
    const facts = [];
    facts.push(`<li><span>${esc(TYPE_INFO[result.type].label)}</span><span>${esc(row.item.name)}</span></li>`);
    facts.push(`<li><span>Title check</span><span>${esc(titleCheckText(row))}</span></li>`);
    if (fit.cut) facts.push(`<li><span>Likely cut after</span><span>${quote(esc(fit.visible))}</span></li>`);
    if (row.sameTitle) facts.push(`<li><span>Same title</span><span>${esc(rowsWord(row.sameTitle.rows.filter(index => index !== row.index)))}</span></li>`);
    if (row.description.status !== 'none') facts.push(`<li><span>Description</span><span>${esc(DESC_STATUS[row.description.status].text)}${row.description.text ? `, ${esc(plural(row.description.chars, 'character'))}` : ''}</span></li>`);
    return `<div class="wsp-detail-inner">${serpMarkup(result, row)}<ul class="wsp-facts">${facts.join('')}</ul></div>`;
  }

  function rowMarkup(result, row, open) {
    const id = `wsp-detail-${row.index}`;
    const { width, chars } = measureWords(result, row);
    const measured = row.title.status === 'unknown' || row.title.status === 'empty' ? '—' : width;
    const sameTitle = row.sameTitle ? `<span class="wsp-same">Same as ${rowsWord(row.sameTitle.rows.filter(index => index !== row.index))}</span>` : '';
    const main = `<tr role="row" class="wsp-row" data-wsp-row="${row.index}" data-status="${row.title.status}"${open ? ' data-open="true"' : ''}>
              <td role="cell" class="wsp-c-n"><span class="wsp-visually-hidden">Row </span>${row.index + 1}</td>
              <td role="cell" class="wsp-c-title">${titleCellMarkup(result, row)}${sameTitle}</td>
              <td role="cell" class="wsp-c-width">${measured}<span class="wsp-chars">${esc(chars)}</span></td>
              <td role="cell" class="wsp-c-status">${chip(row.title.status, TITLE_STATUS[row.title.status].word)}</td>
              ${result.description ? `<td role="cell" class="wsp-c-desc">${descriptionCell(result, row)}</td>` : ''}
              <td role="cell" class="wsp-c-open"><button type="button" class="wsp-open" data-wsp-open="${row.index}" aria-expanded="${open}" aria-controls="${id}"><span class="wsp-open-label">${open ? 'Hide' : 'Preview'}</span><span class="wsp-open-icon" aria-hidden="true"></span><span class="wsp-visually-hidden"> row ${row.index + 1}</span></button></td>
            </tr>`;
    const detail = open ? `<tr role="row" class="wsp-detail" id="${id}"><td role="cell" colspan="${result.description ? 6 : 5}">${detailMarkup(result, row)}</td></tr>` : '';
    return main + detail;
  }

  function tableMarkup(result, view = {}) {
    const rows = visibleRows(result, view);
    const shown = Math.min(rows.length, view.shown || PAGE_SIZE);
    const filter = FILTERS.find(([id]) => id === view.filter);
    if (!rows.length && filter && filter[0] !== 'all') {
      return { html: `<p class="wsp-empty">No titles match ${quote(esc(filter[1]))} now. <button type="button" class="wsp-text-button" data-wsp-filter="all">Show all</button></p>`, total: 0, shown: 0, left: 0 };
    }
    const body = rows.slice(0, shown).map(row => rowMarkup(result, row, view.open === row.index)).join('');
    return {
      html: `<table class="wsp-table" role="table" aria-label="Every title, with its checks">
            <thead role="rowgroup"><tr role="row"><th role="columnheader" scope="col" class="wsp-c-n">#</th><th role="columnheader" scope="col" class="wsp-c-title">Title your pattern makes</th><th role="columnheader" scope="col" class="wsp-c-width">Width</th><th role="columnheader" scope="col" class="wsp-c-status">Title check</th>${result.description ? '<th role="columnheader" scope="col" class="wsp-c-desc">Description</th>' : ''}<th role="columnheader" scope="col" class="wsp-c-open"><span class="wsp-visually-hidden">Preview</span></th></tr></thead>
            <tbody role="rowgroup">${body}</tbody>
          </table>`,
      total: rows.length,
      shown,
      left: rows.length - shown
    };
  }

  // ---- Exports: the problem list and the CSV ----------------------------------------

  function problemListText(result) {
    const { counts, info } = result;
    const lines = [`Wix SEO title preview: ${plural(counts.total, info.one, info.many)}`];
    lines.push(`Title pattern: ${result.titleDefaulted ? `${DEFAULTS[result.type].title} (Wix’s default)` : result.settings.title.trim()}`);
    if (result.siteName) lines.push(`Site name: ${result.siteName}`);
    lines.push(`Description pattern: ${result.description ? result.settings.description.trim() : 'none'}`);
    lines.push('Built from your pattern. Titles set on single products in Edit SEO Settings aren’t included.');
    lines.push('');
    const before = lines.length;
    // A short list to share; the CSV download has every row.
    const LIMIT = 100;
    const section = (heading, rows, describe) => {
      if (!rows.length) return;
      lines.push(`${heading} (${number(rows.length)}):`);
      rows.slice(0, LIMIT).forEach(row => lines.push(`- Row ${row.index + 1}: ${describe(row)}`));
      if (rows.length > LIMIT) lines.push(`- …and ${number(rows.length - LIMIT)} more. Download the CSV for all of them.`);
      lines.push('');
    };
    section('Likely cut on desktop (wider than about 600 px)', result.rows.filter(row => row.title.status === 'cut'), row => {
      const fit = fitFor(result, row);
      return `${row.title.text} (${number(Math.round(row.title.width))} px, ${plural(row.title.chars, 'character')}; would show about as “${fit.visible}${ELLIPSIS}”)`;
    });
    section(`Longer than Wix’s advice of ${info.title.words}, but likely fits on desktop`, result.rows.filter(row => row.title.status === 'long'), row => `${row.title.text} (${plural(row.title.chars, 'character')})`);
    result.sameTitle.slice(0, LIMIT).forEach(group => {
      lines.push(`Same title on ${number(group.rows.length)} ${info.many}: “${result.rows[group.rows[0]].title.text}” (${rowsWord(group.rows)})`);
    });
    if (result.sameTitle.length > LIMIT) lines.push(`…and ${plural(result.sameTitle.length - LIMIT, 'more group')} of repeated titles. Download the CSV for all of them.`);
    if (result.sameTitle.length) lines.push('');
    section('No title', result.rows.filter(row => row.title.status === 'empty'), row => row.item.name);
    if (result.description) {
      section(`No description in your ${listWord(result)}`, result.rows.filter(row => row.description.status === 'empty'), row => row.item.name);
      section(`Description longer than Wix’s advice of ${info.desc.words}`, result.rows.filter(row => row.description.status === 'long'), row => `${row.item.name} (${plural(row.description.chars, 'character')})`);
      result.sameDescription.slice(0, LIMIT).forEach(group => {
        lines.push(`Same description on ${number(group.rows.length)} ${info.many}: ${rowsWord(group.rows)}`);
      });
      if (result.sameDescription.length > LIMIT) lines.push(`…and ${plural(result.sameDescription.length - LIMIT, 'more group')} of repeated descriptions. Download the CSV for all of them.`);
      if (result.sameDescription.length) lines.push('');
    }
    if (lines.length === before) lines.push(result.description ? 'No titles or descriptions look cut, long, empty or repeated.' : 'No titles look cut, long or repeated.', '');
    lines.push('Widths are estimates: measured in an Arial-like font at 20 px against about 600 px. Google publishes no title length and may show other text from the page.');
    return lines.join('\n');
  }

  // Spreadsheet apps can run a cell that starts with =, +, -, @, a tab or a
  // carriage return as a formula; a leading ' keeps it text.
  const safeCell = value => (/^[=+\-@\t\r]/.test(text(value)) ? `'${value}` : text(value));

  function resultsCsv(result) {
    const rows = [['Row', 'Name', 'Title from your pattern', 'Title characters', 'Title width (px, estimate)', 'Title check', 'Shown before the cut (estimate)', 'Same title as rows', 'Description', 'Description characters', 'Description check', 'Same description as rows']];
    result.rows.forEach(row => {
      const fit = fitFor(result, row);
      const others = group => (group ? group.rows.filter(index => index !== row.index).map(index => index + 1).join(' ') : '');
      rows.push([
        row.index + 1,
        safeCell(row.item.name),
        safeCell(row.title.text),
        row.title.chars,
        row.title.status === 'unknown' || !row.title.text ? '' : Math.round(row.title.width),
        titleCheckText(row),
        fit.cut ? safeCell(fit.visible + ELLIPSIS) : '',
        others(row.sameTitle),
        safeCell(row.description.text),
        row.description.status === 'none' ? '' : row.description.chars,
        DESC_STATUS[row.description.status].text,
        others(row.sameDescription)
      ]);
    });
    return shared.toCsv(rows, { bom: true });
  }

  // ---- Inputs markup shared by the page and the browser -------------------------------

  // Three variables up front; the rest wait behind "+N more" so the row
  // stays short on a phone. Wix-only variables are dashed.
  function chipsMarkup(type, field) {
    const variables = VARIABLES[type];
    // Title buttons lead with the name; description buttons with the description.
    const order = field === 'description'
      ? variables.slice().sort((a, b) => (b.fill === 'description') - (a.fill === 'description'))
      : variables;
    const filled = order.filter(variable => variable.fill);
    const front = filled.slice(0, 3);
    const rest = filled.slice(3).concat(order.filter(variable => !variable.fill));
    const button = variable => `<button type="button" class="wsp-var${variable.fill ? '' : ' wsp-var-wix'}" data-wsp-insert="${esc(variable.name)}" data-wsp-field="${field}"${variable.help ? ` title="${esc(variable.help)}"` : ''}><span class="wsp-visually-hidden">Add </span>${esc(variable.name)}</button>`;
    const wixOnly = rest.some(variable => !variable.fill);
    // The extra buttons come before the toggle, so "Show fewer" ends the row.
    const more = rest.length
      ? `<span class="wsp-var-extra" id="wsp-${field}-more" hidden>${rest.map(button).join('')}</span><button type="button" class="wsp-var-more" data-wsp-more="${field}" aria-expanded="false" aria-controls="wsp-${field}-more">+${rest.length} more</button>${wixOnly ? `<span class="wsp-var-note" data-wsp-more-note="${field}" hidden>Dashed ones are filled in by Wix; the preview shows a placeholder.</span>` : ''}`
      : '';
    return `${front.map(button).join('')}${more}`;
  }

  function fieldIssuesMarkup(parsed, field = 'title') {
    if (!parsed || !parsed.issues.length) return '';
    const seen = new Set();
    return parsed.issues.filter(issue => {
      const key = issue.kind + issue.text;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(issue => {
      const many = esc(TYPE_INFO[parsed.type].many);
      if (issue.kind === 'unknown') {
        if (issue.other) return `<p data-kind="unknown">${code(issue.text)} is a variable for ${esc(TYPE_INFO[issue.other].many)}, not ${many}.</p>`;
        return `<p data-kind="unknown">${code(issue.text)} isn’t a variable this preview knows. If it’s in Wix’s + Add Variable list, Wix fills it in. Otherwise, check the spelling.</p>`;
      }
      if (issue.kind === 'general') return `<p data-kind="general">${code(issue.text)} is in Wix’s variable list, and Wix fills it in, so the preview shows a placeholder. Check that + Add Variable offers it for ${many}.</p>`;
      if (issue.kind === 'page-only') return `<p data-kind="page-only">This preview fills in ${code(issue.text)} only for main pages. If + Add Variable in Wix offers it for ${many}, Wix fills it in.</p>`;
      if (issue.kind === 'stray') return `<p data-kind="stray">Extra braces in ${joinWords((issue.parts || [issue.text]).map(part => code(part)))} show as plain text in the ${field === 'description' ? 'description' : 'title'}.</p>`;
      if (issue.kind === 'empty') return `<p data-kind="empty">${code(issue.text)} is empty. Put a variable name between the braces.</p>`;
      if (issue.kind === 'unclosed') return `<p data-kind="unclosed">A ${code('{{')} isn’t closed. Variables look like ${code(variableText(nameVariable(parsed.type)))}.</p>`;
      if (issue.kind === 'single') return `<p data-kind="single">${code(issue.text)} has single braces, so it’s plain text. <button type="button" class="wsp-fix" data-wsp-action="double-braces" data-wsp-field="${field === 'description' ? 'description' : 'title'}" data-wsp-from="${esc(issue.text)}" data-wsp-to="${esc(issue.fix)}">Use ${esc(issue.fix)}</button></p>`;
      if (issue.kind === 'shorthand') return `<p data-kind="shorthand">Read the way Wix’s help writes it: ${code(issue.fix)}.</p>`;
      return '';
    }).join('');
  }

  function fileCardMarkup(file, type) {
    if (!file) return '';
    const info = TYPE_INFO[type];
    const lines = [];
    // What the rows are follows the file (a categories export stays
    // categories whatever page type is picked).
    const [one, many] = file.kind === 'categories' ? ['category', 'categories'] : file.kind === 'products' ? ['product', 'products'] : [info.one, info.many];
    if (file.ok) {
      const bits = [`<strong>${esc(plural(file.products.length, one, many))}</strong>`];
      const skipped = file.counts.variants + file.counts.media;
      if (skipped) bits.push(`${number(skipped)} variant${file.counts.media ? ' and media' : ''} ${skipped === 1 ? 'row' : 'rows'} skipped`);
      if (file.counts.noName) bits.push(`${plural(file.counts.noName, 'row')} without a name skipped`);
      if (file.counts.other) bits.push(`${plural(file.counts.other, 'other row')} skipped`);
      lines.push(`<p class="wsp-file-counts">${bits.join(' · ')}</p>`);
      if (file.truncated) lines.push(`<p class="wsp-file-warn">Showing the first ${number(file.truncated.kept)} of ${number(file.truncated.total)}. This page previews up to ${number(MAX_PRODUCTS)} at a time.</p>`);
      if (file.html) lines.push(`<p class="wsp-file-note">HTML tags removed from ${plural(file.html, 'description')}.</p>`);
      if (file.format === 'other' && file.columnNames.name) lines.push(`<p class="wsp-file-note">Names from the ${quote(esc(file.columnNames.name))} column${file.columnNames.description ? `, descriptions from ${quote(esc(file.columnNames.description))}` : ''}.</p>`);
      if (file.counts.hidden) {
        const why = file.kind === 'categories'
          ? 'Wix says an inactive category no longer has a URL, so this preview leaves out inactive categories.'
          : file.kind === 'products'
            ? 'Wix says a hidden product’s page shows a 404 “page not found” error, so this preview leaves out hidden products.'
            : 'Rows marked not visible were left out.';
        const label = file.kind === 'categories' ? plural(file.counts.hidden, 'inactive category', 'inactive categories') : plural(file.counts.hidden, `hidden ${one}`, `hidden ${many}`);
        lines.push(`<details class="wsp-hidden-list"><summary>${esc(label)} skipped<span class="wsp-disclosure-icon" aria-hidden="true"></span></summary><p>${why}</p><ul>${file.hidden.map(name => `<li>${esc(name)}</li>`).join('')}${file.counts.hidden > file.hidden.length ? `<li>and ${number(file.counts.hidden - file.hidden.length)} more</li>` : ''}</ul></details>`);
      }
    } else if (file.reason === 'no-products') {
      lines.push(`<p class="wsp-file-warn" role="alert">No ${esc(many)} found in this file${file.counts && (file.counts.variants || file.counts.media) ? ': it only has variant or media rows' : ''}.</p>`);
    } else if (file.reason === 'not-csv') {
      lines.push(`<p class="wsp-file-warn" role="alert">This looks like a ${esc(file.what)}, not a CSV file. Load the CSV file that Wix’s export downloads.</p>`);
    }
    if (file.unclosedQuote) lines.push('<p class="wsp-file-warn">A quote mark in this file is never closed, so the rest of the file may have been read as one cell. Save the file as CSV from your spreadsheet app and load it again.</p>');
    if (file.garbled) lines.push('<p class="wsp-file-warn">Some letters look garbled, like “Ã©” for “é”. Save the file as UTF-8 CSV from Google Sheets or Excel and try again.</p>');
    if (file.encoding === 'windows-1252') lines.push('<p class="wsp-file-note">This file isn’t UTF-8, so it was read as Western (Windows-1252) text. If letters look wrong, save it as UTF-8 CSV and try again.</p>');
    const switchTo = file.ok && file.kind === 'categories' && type === 'product' ? 'category' : file.ok && file.kind === 'products' && type !== 'product' ? 'product' : null;
    if (switchTo) lines.push(`<p class="wsp-file-note">This looks like a ${switchTo === 'category' ? 'categories' : 'products'} export. <button type="button" class="wsp-fix" data-wsp-action="switch-type" data-wsp-type="${switchTo}">Switch to ${esc(TYPE_INFO[switchTo].label)}</button></p>`);
    return `<div class="wsp-file-head"><p class="wsp-file-name"><span class="wsp-file-icon" aria-hidden="true"></span><span><span class="wsp-visually-hidden">File: </span>${esc(file.name || 'products.csv')}</span></p><button type="button" class="wsp-text-button" data-wsp-action="remove-file">Remove file</button></div>
          ${file.ok ? `<p class="wsp-file-format">Read as: ${esc(file.formatLabel)}${file.picked ? ', with the columns you picked' : ''}</p>` : ''}
          ${lines.join('')}
          ${file.headers && file.headers.length > 1 && file.reason !== 'no-name-column' && !file.picking ? `<button type="button" class="wsp-text-button" data-wsp-action="pick-columns">Choose columns</button>` : ''}`;
  }

  function pickerMarkup(file, type) {
    if (!file || !file.headers || !file.headers.length) return '';
    const option = (index, selected) => {
      const sample = file.sample && file.sample[index] ? ` (${file.sample[index].slice(0, 32)})` : '';
      return `<option value="${index}"${selected ? ' selected' : ''}>${esc((file.headers[index] || `Column ${index + 1}`) + sample)}</option>`;
    };
    const nameAt = file.columns && file.columns.name >= 0 ? file.columns.name : -1;
    const descAt = file.columns && file.columns.description >= 0 ? file.columns.description : -1;
    const options = selected => file.headers.map((_, index) => option(index, index === selected)).join('');
    const lead = file.reason === 'no-name-column'
      ? `<p class="wsp-picker-lead" role="alert">This file has no ${code('name')} column, so it isn’t a Wix export we recognize. Pick the column that holds each ${esc(TYPE_INFO[type].listOne)}.</p>`
      : '<p class="wsp-picker-lead">Pick the columns to read.</p>';
    return `${lead}
          <div class="wsp-picker-fields">
            <div class="wsp-field"><label for="wsp-pick-name">Name column</label><select id="wsp-pick-name" data-wsp-pick="name">${nameAt === -1 ? '<option value="" selected>Choose a column</option>' : ''}${options(nameAt)}</select></div>
            <div class="wsp-field"><label for="wsp-pick-description">Description column <span class="wsp-optional">Optional</span></label><select id="wsp-pick-description" data-wsp-pick="description"><option value=""${descAt === -1 ? ' selected' : ''}>None</option>${options(descAt)}</select></div>
          </div>
          <button type="button" class="wsp-button" data-wsp-action="apply-columns">Use these columns</button>`;
  }

  // ---- The sample (a made-up store) ---------------------------------------------------

  const SAMPLE_STORE = 'Juniper & Oak Candle Co.';
  // Shown beside the drawn result while the sample is loaded.
  const SAMPLE_LABEL = 'Sample store (made up)';
  const SAMPLE_LISTS = Object.freeze({
    product: Object.freeze([
      ['Hand-Poured Lavender & Cedarwood Soy Candle, 16 oz', '34', 'Lavender and cedarwood in a 16 oz soy candle, poured by hand in small batches. Burns for about 80 hours with a cotton wick, in an amber glass jar you can reuse.'],
      ['Vanilla Bean Candle', '18', 'Warm vanilla bean in an 8 oz soy candle. Burns for about 45 hours.'],
      ['Vanilla Bean Candle', '28', 'Warm vanilla bean in our 16 oz soy candle. Burns for about 80 hours.'],
      ['Fig & Black Pepper Wax Melts', '12', 'Hand-poured in small batches and packed in recyclable paper.'],
      ['Gift Card', '25', ''],
      ['Sea Salt & Driftwood Reed Diffuser with Rattan Sticks', '42', 'Sea salt and driftwood in a glass reed diffuser with eight rattan sticks. Lasts about three months.'],
      ['Wick Trimmer', '14', 'Trim wicks to 1/4 inch before each burn for a clean, even flame.'],
      ['Midnight Amber 3-Wick Candle 🕯\ufe0f', '38', 'Amber, smoked vanilla and black tea in a 3-wick candle for bigger rooms.'],
      ['Little Tin Trio: Fir, Lilac & Fig, 4 oz', '24', 'Three 4 oz travel tins: fir, lilac and fig. Each burns for about 20 hours.'],
      ['Beeswax Taper Candles, Set of 2', '16', 'Hand-poured in small batches and packed in recyclable paper.'],
      ['Ceramic Vessel – Sage Green', '36', 'A handmade ceramic vessel in sage green. Use it as a planter when the candle is done.'],
      ['Autumn Harvest Collection: Spiced Apple, Pumpkin Chai & Maple Bourbon Candles', '79', 'Spiced apple, pumpkin chai and maple bourbon: three 8 oz candles in a gift box. Made in a small run each fall.']
    ].map(([name, price, description]) => Object.freeze({ name, price, description }))),
    category: Object.freeze([
      ['All Products', ''],
      ['Soy Candles', 'Hand-poured soy candles in amber jars, from 8 oz to 16 oz.'],
      ['Wax Melts & Diffusers', 'Wax melts and reed diffusers for scent without a flame.'],
      ['Gift Sets', 'Candle sets in a gift box, ready to give.'],
      ['Candle Care & Accessories', 'Wick trimmers, snuffers and lids to help candles burn evenly.'],
      ['Seasonal Favorites: Autumn Harvest & Holiday Collections', 'Limited scents for fall and winter, poured in small runs.']
    ].map(([name, description]) => Object.freeze({ name, description }))),
    post: Object.freeze([
      ['How to Get an Even Burn From a New Candle', 'The first burn sets the pattern for every burn after it. Here is how long to let it go.'],
      ['Soy vs. Beeswax: Which Candle Wax Is Right for You?', 'How soy and beeswax candles differ in burn time, scent and price.'],
      ['5 Ways to Reuse an Empty Candle Jar', ''],
      ['Why Does My Candle Tunnel? Causes and How to Fix It', 'Tunneling starts on the first burn. Here is how to fix a candle that already has it.'],
      ['Behind the Scenes: Pouring 500 Candles for the Holiday Market in One Weekend', 'What a weekend of pouring looks like in our small studio.'],
      ['Candle Safety Tips', 'Simple rules for burning candles safely at home.']
    ].map(([name, description]) => Object.freeze({ name, description }))),
    page: Object.freeze(['Home', 'About', 'Contact', 'FAQ', 'Shipping & Returns', 'Wholesale Inquiries'].map(name => Object.freeze({ name })))
  });

  // The sample as it sits in the paste box: one line each, tabs between the
  // name, price and description, as when pasted from a spreadsheet.
  function sampleText(type = 'product') {
    return SAMPLE_LISTS[type].map(item => [item.name, type === 'product' ? item.price : null, item.description].filter(value => value != null && value !== '').join('\t')).join('\n');
  }

  const SAMPLE = Object.freeze({
    store: SAMPLE_STORE,
    label: SAMPLE_LABEL,
    settings: Object.freeze({ type: 'product', siteName: SAMPLE_STORE, title: DEFAULTS.product.title, description: DEFAULTS.product.description }),
    products: SAMPLE_LISTS.product,
    lists: SAMPLE_LISTS
  });

  // describe(input): the summary sentence for a preview result, or for a
  // { settings, products } input such as SAMPLE.
  function describe(input = SAMPLE, measure) {
    if (input && Array.isArray(input.rows)) return summaryText(input);
    const settings = (input && input.settings) || SAMPLE.settings;
    const products = (input && input.products) || SAMPLE.products;
    return summaryText(preview(settings, products, measure));
  }

  // The /tools card: the sample's widest title, drawn, the summary, and the
  // label the tool shows beside the drawing while the sample is loaded.
  function directoryPreview() {
    const result = preview(SAMPLE.settings, SAMPLE.products, approximateWidth);
    const row = widest(result);
    const data = serpData(result, row);
    return { title: data.title, widthStatus: data.widthStatus, url: data.url, snippet: data.snippet, summary: summaryText(result), cutAfter: data.cutAfter, sampleName: SAMPLE_STORE, sampleLabel: SAMPLE_LABEL };
  }

  // ---- Page behaviour ----------------------------------------------------------------

  function readFile(file) {
    const decode = buffer => {
      const bytes = new Uint8Array(buffer);
      if (bytes[0] === 0xff && bytes[1] === 0xfe) return { text: new TextDecoder('utf-16le').decode(bytes), encoding: 'utf-16' };
      if (bytes[0] === 0xfe && bytes[1] === 0xff) return { text: new TextDecoder('utf-16be').decode(bytes), encoding: 'utf-16' };
      // UTF-16 without the mark: every other byte of plain letters is zero.
      if (bytes.length > 3 && bytes[0] && !bytes[1] && bytes[2] && !bytes[3]) return { text: new TextDecoder('utf-16le').decode(bytes), encoding: 'utf-16' };
      if (bytes.length > 3 && !bytes[0] && bytes[1] && !bytes[2] && bytes[3]) return { text: new TextDecoder('utf-16be').decode(bytes), encoding: 'utf-16' };
      try { return { text: new TextDecoder('utf-8', { fatal: true }).decode(bytes), encoding: 'utf-8' }; } catch (_) {
        return { text: new TextDecoder('windows-1252').decode(bytes), encoding: 'windows-1252' };
      }
    };
    if (typeof file.arrayBuffer === 'function' && typeof TextDecoder === 'function') return file.arrayBuffer().then(decode);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ text: String(reader.result || ''), encoding: 'utf-8' });
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  function init(root) {
    if (!root || root.dataset.wspReady === 'true') return;
    root.dataset.wspReady = 'true';
    const doc = root.ownerDocument;
    const win = doc.defaultView || window;
    const find = name => root.querySelector(`[data-wsp-${name}]`);
    const els = {
      site: find('site'), title: find('title'), description: find('description'), list: find('list'),
      titleChips: find('chips="title"'), descriptionChips: find('chips="description"'),
      titleIssues: find('issues="title"'), descriptionIssues: find('issues="description"'),
      listLabel: find('list-label'), listCount: find('list-count'), listHelp: find('list-help'), paste: find('paste'),
      file: find('file'), fileButton: find('file-button'), fileButtonWrap: find('file-button-wrap'), fileCard: find('file-card'), picker: find('picker'), fileStatus: find('file-status'),
      sampleNote: find('sample-note'), sampleTag: find('sample-tag'), drawHead: find('draw-head'), siteHint: find('site-hint'),
      summary: find('summary'), which: find('which'), serp: find('serp'), changes: find('changes'), changesBlock: find('changes-block'),
      filters: find('filters'), sort: find('sort'), table: find('table'), more: find('show-more'), results: find('results'),
      status: find('status'), fallback: find('fallback'), fallbackText: find('fallback-text'), announcer: find('announcement'),
      copy: find('copy'), download: find('download')
    };
    if (!els.site || !els.title || !els.description || !els.list || !els.table) return;
    const measure = shared.makeMeasure(doc);
    const radios = () => Array.from(root.querySelectorAll('input[name="wsp-type"]'));
    const checkedType = () => (radios().find(input => input.checked) || {}).value;
    const state = {
      type: TYPES.includes(checkedType()) ? checkedType() : 'product',
      siteName: els.site.value,
      slots: {},
      filter: 'all',
      sort: null,
      shown: PAGE_SIZE,
      open: null,
      pick: 'longest'
    };
    TYPES.forEach(type => { state.slots[type] = { title: DEFAULTS[type].title, description: DEFAULTS[type].description, text: sampleText(type), file: null, parsed: null }; });
    // A browser restoring the form after Back brings typed values with it.
    Object.assign(state.slots[state.type], { title: els.title.value, description: els.description.value, text: els.list.value });
    let result = null;
    let announceTimer = 0;
    let renderTimer = 0;
    let reported = false;
    const caret = { title: null, description: null };
    const coarse = () => typeof win.matchMedia === 'function' && win.matchMedia('(pointer: coarse)').matches;
    const toolEvent = name => doc.dispatchEvent(new win.CustomEvent('myuser:tool-event', { detail: { name } }));
    const shown = new Map();
    function setHtml(element, html) {
      if (!element || shown.get(element) === html) return;
      shown.set(element, html);
      element.innerHTML = html;
    }
    const setStatus = message => { if (els.status) els.status.textContent = message; };

    const slot = () => state.slots[state.type];
    function products() {
      const current = slot();
      if (current.file) return current.file.ok ? current.file.products : [];
      if (!current.parsed || current.parsed.source !== current.text) current.parsed = { source: current.text, ...parseProducts(current.text, state.type) };
      return current.parsed.products;
    }

    function renderList() {
      const current = slot();
      const info = TYPE_INFO[state.type];
      const file = current.file;
      els.paste.hidden = Boolean(file);
      if (els.fileCard) {
        setHtml(els.fileCard, file ? fileCardMarkup(file, state.type) : '');
        els.fileCard.hidden = !file;
      }
      if (els.picker) {
        const showPicker = Boolean(file && (file.reason === 'no-name-column' || file.picking));
        setHtml(els.picker, showPicker ? pickerMarkup(file, state.type) : '');
        els.picker.hidden = !showPicker;
      }
      const list = products();
      const parsed = current.parsed;
      let count = file ? (file.ok ? `${plural(list.length, info.one, info.many)} from the file` : 'Waiting for columns') : plural(list.length, info.one, info.many);
      if (!file && parsed && parsed.truncated) count = `First ${number(parsed.truncated.kept)} of ${number(parsed.truncated.total)} ${info.many}`;
      if (els.listCount) els.listCount.textContent = count;
      const ownList = Boolean(file) || current.text !== sampleText(state.type);
      if (els.sampleNote) els.sampleNote.hidden = ownList;
      if (els.sampleTag) els.sampleTag.hidden = ownList || !list.length;
      // Their own list with the sample's site name: say so beside the field.
      if (els.siteHint) els.siteHint.hidden = !(ownList && list.length && tidyText(state.siteName) === SAMPLE_STORE);
    }

    function renderTypeParts() {
      const info = TYPE_INFO[state.type];
      root.dataset.wspType = state.type;
      setHtml(els.titleChips, chipsMarkup(state.type, 'title'));
      setHtml(els.descriptionChips, chipsMarkup(state.type, 'description'));
      if (els.listLabel) els.listLabel.textContent = info.listLabel;
      if (els.fileButton) els.fileButton.textContent = info.file;
      if (els.listHelp) els.listHelp.textContent = listHelp(state.type);
      root.querySelectorAll('[data-wsp-type-text]').forEach(node => { node.hidden = node.dataset.wspTypeText !== state.type; });
    }

    function compute() {
      const current = slot();
      const file = current.file;
      const source = file && file.ok ? { kind: 'file', format: file.format, has: file.has } : { kind: 'list' };
      result = preview({ type: state.type, siteName: state.siteName, title: current.title, description: current.description, source }, products(), measure);
      return result;
    }

    function renderResult() {
      const list = result.rows;
      const waiting = slot().file && !slot().file.ok;
      setHtml(els.summary, waiting
        ? `<p class="wsp-summary">${slot().file.reason === 'no-name-column' ? 'Choose the column with the names to see their titles.' : `No ${esc(TYPE_INFO[state.type].many)} found in that file yet.`}</p>`
        : summaryMarkup(result));
      if (els.drawHead) els.drawHead.hidden = !list.length;
      const pickRow = !list.length ? null : state.pick === 'shortest' ? narrowest(result) : widest(result);
      if (els.which) {
        els.which.hidden = list.length < 2;
        els.which.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.wspWhichValue === state.pick)));
      }
      setHtml(els.serp, pickRow ? serpMarkup(result, pickRow) : '');
      els.serp.hidden = !pickRow;
      setHtml(els.changes, suggestionsMarkup(result));
      if (els.changesBlock) els.changesBlock.hidden = !els.changes.innerHTML;
      setHtml(els.filters, filtersMarkup(result, { filter: state.filter }));
      els.filters.hidden = !els.filters.innerHTML;
      if (els.sort) {
        els.sort.setAttribute('aria-pressed', String(state.sort === 'width'));
        els.sort.hidden = list.length < 2;
      }
      const table = tableMarkup(result, { filter: state.filter, sort: state.sort, shown: state.shown, open: state.open });
      setHtml(els.table, table.html);
      if (els.more) {
        els.more.hidden = table.left <= 0;
        els.more.textContent = `Show ${number(Math.min(PAGE_SIZE, table.left))} more (${number(table.left)} left)`;
      }
      if (els.results) els.results.hidden = !list.length;
      if (els.copy) els.copy.disabled = !list.length;
      if (els.download) els.download.disabled = !list.length;
      // Wix: empty the title field and save to go back to its default.
      const titleNotes = result.titleDefaulted
        ? `<p data-kind="default">An empty title field goes back to Wix’s default when you save it, so this shows ${code(DEFAULTS[state.type].title)}.</p>`
        : fieldIssuesMarkup(result.title, 'title');
      setHtml(els.titleIssues, titleNotes);
      els.titleIssues.hidden = !titleNotes;
      const descriptionNotes = fieldIssuesMarkup(result.description, 'description');
      setHtml(els.descriptionIssues, descriptionNotes);
      els.descriptionIssues.hidden = !descriptionNotes;
      root.querySelectorAll('[data-wsp-reset]').forEach(button => {
        const field = button.dataset.wspReset;
        button.disabled = slot()[field] === DEFAULTS[state.type][field];
      });
      const clear = find('clear');
      if (clear) clear.disabled = !slot().text && !slot().file;
    }

    function render({ announce = false, user = false } = {}) {
      compute();
      renderList();
      renderResult();
      if (user && !reported) { reported = true; toolEvent('wix_patterns_previewed'); }
      if (announce && els.announcer) {
        clearTimeout(announceTimer);
        announceTimer = setTimeout(() => { els.announcer.textContent = summaryText(result); }, 900);
      }
    }

    // Big lists wait for a pause in typing; small ones update at once. A new
    // list starts back at the top of the table; a pattern edit keeps the
    // open preview open, so it changes in view.
    function changed(listChanged = false) {
      setStatus('');
      hideFallback();
      if (listChanged) {
        state.shown = PAGE_SIZE;
        state.open = null;
      }
      clearTimeout(renderTimer);
      if (products().length > 1500) renderTimer = setTimeout(() => render({ announce: true, user: true }), 220);
      else render({ announce: true, user: true });
    }

    function remember(input) {
      const field = input === els.title ? 'title' : input === els.description ? 'description' : null;
      if (!field) return;
      try { caret[field] = { start: input.selectionStart, end: input.selectionEnd }; } catch (_) { caret[field] = null; }
    }

    function insertVariable(field, name) {
      const input = field === 'description' ? els.description : els.title;
      const value = input.value;
      const at = caret[field] && caret[field].start != null ? caret[field] : { start: value.length, end: value.length };
      const before = value.slice(0, at.start);
      const after = value.slice(at.end);
      const lead = before && !/\s$/.test(before) ? ' ' : '';
      const tail = after && !/^\s/.test(after) ? ' ' : '';
      const insert = `${lead}${variableText(name)}${tail}`;
      input.value = before + insert + after;
      const position = before.length + insert.length - tail.length;
      caret[field] = { start: position, end: position };
      slot()[field] = input.value;
      if (!coarse()) {
        input.focus();
        try { input.setSelectionRange(position, position); } catch (_) { /* not selectable */ }
      }
      changed();
    }

    function setPattern(field, value) {
      const input = field === 'description' ? els.description : els.title;
      input.value = value;
      slot()[field] = value;
      caret[field] = { start: value.length, end: value.length };
      changed();
    }

    function switchType(type) {
      if (!TYPES.includes(type) || type === state.type) return;
      state.type = type;
      state.filter = 'all';
      state.sort = null;
      radios().forEach(input => { input.checked = input.value === type; });
      els.title.value = slot().title;
      els.description.value = slot().description;
      els.list.value = slot().text;
      caret.title = null;
      caret.description = null;
      renderTypeParts();
      changed(true);
    }

    function hideFallback() { if (els.fallback) els.fallback.hidden = true; }

    async function copy(value, message) {
      hideFallback();
      try {
        if (!win.navigator.clipboard || typeof win.navigator.clipboard.writeText !== 'function') throw new Error('Clipboard unavailable');
        await win.navigator.clipboard.writeText(value);
        setStatus(message);
      } catch (_) {
        if (!els.fallback || !els.fallbackText) { setStatus('Couldn’t copy automatically.'); return; }
        els.fallbackText.value = value;
        els.fallback.hidden = false;
        els.fallbackText.focus();
        els.fallbackText.select();
        try { els.fallbackText.setSelectionRange(0, value.length); } catch (__) { /* not selectable */ }
        let copied = false;
        try { copied = typeof doc.execCommand === 'function' && doc.execCommand('copy'); } catch (__) { copied = false; }
        if (copied) { els.fallback.hidden = true; if (els.copy) els.copy.focus(); }
        setStatus(copied ? message : 'Couldn’t copy automatically. The list is selected in the box below, so copy it from there.');
      }
    }

    function download(content, filename) {
      if (!win.URL || typeof win.URL.createObjectURL !== 'function') {
        copy(content, 'Download isn’t available here, so the results were copied instead.');
        return;
      }
      const url = win.URL.createObjectURL(new win.Blob([content], { type: 'text/csv;charset=utf-8' }));
      const link = doc.createElement('a');
      link.href = url;
      link.download = filename;
      link.hidden = true;
      root.append(link);
      link.click();
      link.remove();
      setTimeout(() => win.URL.revokeObjectURL(url), 1000);
      setStatus('Results downloaded.');
    }

    function fileStatus(message) {
      if (!els.fileStatus) return;
      els.fileStatus.textContent = message;
      els.fileStatus.hidden = !message;
    }

    function useFile(file, parsed, encoding) {
      const current = slot();
      current.file = { ...parsed, name: file.name, encoding, source: parsed.source };
      state.filter = 'all';
      fileStatus('');
      if (parsed.ok) toolEvent('wix_patterns_csv_loaded');
      changed(true);
    }

    async function loadFile(file) {
      if (!file) return;
      if (file.size > MAX_FILE_BYTES) {
        fileStatus(`This file is ${(file.size / 1048576).toFixed(1)} MB, more than the 25 MB this page can read in your browser. Export fewer products at a time, or paste the names instead.`);
        return;
      }
      fileStatus(`Reading ${file.name}…`);
      let read;
      try { read = await readFile(file); } catch (_) {
        fileStatus('Couldn’t read that file. Try saving it again as a CSV file.');
        return;
      }
      // Let the message paint before the (synchronous) parse.
      await new Promise(resolve => setTimeout(resolve, 30));
      const parsed = parseWixProductsCsv(read.text);
      parsed.source = read.text;
      useFile(file, parsed, read.encoding);
    }

    root.addEventListener('input', event => {
      const target = event.target;
      if (target === els.site) { state.siteName = target.value; changed(); return; }
      if (target === els.title || target === els.description) {
        slot()[target === els.title ? 'title' : 'description'] = target.value;
        remember(target);
        changed();
        return;
      }
      if (target === els.list) { slot().text = target.value; slot().file = null; changed(true); }
    });
    ['select', 'keyup', 'click', 'blur'].forEach(name => {
      root.addEventListener(name, event => { if (event.target === els.title || event.target === els.description) remember(event.target); }, true);
    });
    root.addEventListener('change', event => {
      const target = event.target;
      if (target.name === 'wsp-type') { switchType(target.value); return; }
      if (target === els.file) {
        const file = target.files && target.files[0];
        loadFile(file);
        target.value = '';
      }
    });
    root.addEventListener('click', event => {
      const target = event.target.closest('button, [data-wsp-row]');
      if (!target || !root.contains(target)) return;
      if (target.dataset.wspInsert) { insertVariable(target.dataset.wspField, target.dataset.wspInsert); return; }
      if (target.dataset.wspMore) {
        const extra = root.querySelector(`#wsp-${target.dataset.wspMore}-more`);
        const note = root.querySelector(`[data-wsp-more-note="${target.dataset.wspMore}"]`);
        const open = target.getAttribute('aria-expanded') !== 'true';
        target.setAttribute('aria-expanded', String(open));
        if (extra) extra.hidden = !open;
        if (note) note.hidden = !open;
        target.textContent = open ? 'Show fewer' : `+${extra ? extra.querySelectorAll('.wsp-var').length : ''} more`;
        // The new buttons come before this one, so take focus to the first.
        const first = open && extra && extra.querySelector('.wsp-var');
        if (first) first.focus();
        return;
      }
      if (target.dataset.wspReset) { setPattern(target.dataset.wspReset, DEFAULTS[state.type][target.dataset.wspReset]); return; }
      if (target.dataset.wspFilter) { state.filter = target.dataset.wspFilter; state.shown = PAGE_SIZE; state.open = null; renderResult(); return; }
      if (target.dataset.wspWhichValue) { state.pick = target.dataset.wspWhichValue; renderResult(); return; }
      if (target === els.sort) { state.sort = state.sort === 'width' ? null : 'width'; state.shown = PAGE_SIZE; renderResult(); return; }
      if (target === els.more) { state.shown += PAGE_SIZE; renderResult(); return; }
      if (target.dataset.wspOpen != null || target.dataset.wspRow != null) {
        // A press on a row (not on its text being selected) opens its preview.
        if (target.dataset.wspRow != null && win.getSelection && String(win.getSelection())) return;
        const index = Number(target.dataset.wspOpen != null ? target.dataset.wspOpen : target.dataset.wspRow);
        state.open = state.open === index ? null : index;
        renderResult();
        const button = root.querySelector(`[data-wsp-open="${index}"]`);
        if (button && target.dataset.wspOpen != null) button.focus();
        return;
      }
      if (target === els.copy) { if (result && result.rows.length) copy(problemListText(result), 'Problem list copied.'); return; }
      if (target === els.download) { if (result && result.rows.length) download(resultsCsv(result), 'wix-seo-titles.csv'); return; }
      if (target === els.fileButtonWrap) { if (els.file) els.file.click(); return; }
      const action = target.dataset.wspAction;
      if (!action) return;
      if (action === 'sample') {
        slot().file = null;
        slot().text = sampleText(state.type);
        els.list.value = slot().text;
        els.site.value = SAMPLE_STORE;
        state.siteName = SAMPLE_STORE;
        setStatus('');
        clearTimeout(renderTimer);
        state.shown = PAGE_SIZE;
        state.open = null;
        render({ announce: true });
        return;
      }
      if (action === 'clear') { slot().file = null; slot().text = ''; els.list.value = ''; changed(true); els.list.focus(); return; }
      if (action === 'remove-file') { slot().file = null; changed(true); els.list.focus(); return; }
      if (action === 'pick-columns') { const file = slot().file; if (file) { file.picking = true; renderList(); const select = root.querySelector('[data-wsp-pick="name"]'); if (select) select.focus(); } return; }
      if (action === 'apply-columns') {
        const file = slot().file;
        const name = root.querySelector('[data-wsp-pick="name"]');
        const desc = root.querySelector('[data-wsp-pick="description"]');
        if (!file || !name || name.value === '') { fileStatus('Choose the column with the names first.'); return; }
        const parsed = parseWixProductsCsv(file.source, { columns: { name: Number(name.value), description: desc && desc.value !== '' ? Number(desc.value) : -1 } });
        parsed.source = file.source;
        useFile({ name: file.name }, parsed, file.encoding);
        return;
      }
      if (action === 'switch-type') {
        const file = slot().file;
        const next = target.dataset.wspType;
        slot().file = null;
        switchType(next);
        if (file) { slot().file = file; changed(true); }
        return;
      }
      if (action === 'use-description') { setPattern('description', variableText(descVariable(state.type))); return; }
      if (action === 'drop-site') { if (result && result.siteCost) setPattern('title', result.siteCost.pattern); return; }
      if (action === 'name-first') {
        const parsed = parsePattern(slot().title || DEFAULTS[state.type].title, state.type);
        const name = variableText(nameVariable(state.type));
        const at = parsed.tokens.findIndex(token => token.kind === 'var' && token.variable.fill === 'name');
        let rest = parsed.tokens;
        let sign = ' | ';
        if (at !== -1) {
          const before = parsed.tokens[at - 1];
          const drop = before && before.kind === 'text' && SEPARATOR_ONLY.test(before.text) && before.text.trim() ? at - 1 : at;
          if (drop < at) sign = before.text;
          rest = parsed.tokens.slice(0, drop).concat(parsed.tokens.slice(at + 1));
        }
        const restText = tokensToPattern(rest).trim();
        setPattern('title', restText ? `${name}${sign}${restText}` : name);
        return;
      }
      if (action === 'double-braces') {
        const field = target.dataset.wspField === 'description' ? 'description' : 'title';
        setPattern(field, slot()[field].split(target.dataset.wspFrom).join(target.dataset.wspTo));
        return;
      }
      if (action === 'focus-site') { els.site.focus(); }
    });
    // Drop a file anywhere on the list block.
    const dropZone = find('drop');
    if (dropZone) {
      dropZone.addEventListener('dragover', event => { event.preventDefault(); dropZone.dataset.drag = 'true'; });
      dropZone.addEventListener('dragleave', () => { delete dropZone.dataset.drag; });
      dropZone.addEventListener('drop', event => {
        event.preventDefault();
        delete dropZone.dataset.drag;
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (file) loadFile(file);
      });
    }
    win.addEventListener('pagehide', () => { clearTimeout(announceTimer); clearTimeout(renderTimer); });
    renderTypeParts();
    render();
  }

  function listHelp(type) {
    if (type === 'product') return 'One per line. To add a price and a description, paste from a spreadsheet: name, price, then description.';
    if (type === 'page') return 'One per line, as each page is named in your site menu.';
    return `One per line. To add a description, paste from a spreadsheet: ${type === 'post' ? 'title' : 'name'}, then description.`;
  }

  return Object.freeze({
    META,
    TYPES,
    TYPE_INFO,
    DEFAULTS,
    VARIABLES,
    TITLE_STATUS,
    DESC_STATUS,
    MAX_PRODUCTS,
    MAX_FILE_BYTES,
    TITLE_WIDTH,
    SAMPLE,
    sampleText,
    listHelp,
    parsePattern,
    applyPattern,
    tokensToPattern,
    parseProducts,
    parseWixProductsCsv,
    cleanDescription,
    decodeEntities,
    countChars,
    fitLine,
    cutSnippet,
    preview,
    fitFor,
    widest,
    narrowest,
    summaryText,
    summaryMarkup,
    descriptionLine,
    serpData,
    serpMarkup,
    suggestions,
    suggestionsMarkup,
    filtersMarkup,
    tableMarkup,
    chipsMarkup,
    fieldIssuesMarkup,
    fileCardMarkup,
    pickerMarkup,
    problemListText,
    resultsCsv,
    describe,
    directoryPreview,
    plainText,
    esc,
    init
  });
});
